// NodePanel.js — the click-a-node detail view (Sos2 right pane), 12-axis D·E·R.
// Two parts: (1) a scannable 12-axis SCOREBOARD — one bar + one percentile per
// axis, grouped Design/Execution/Read; (2) the interactive heart — the
// PRE-GENERATED selectable ACTION PATHS (der_prose pass): repairs, optional pushes,
// and protect moves all open the same modal pattern with different urgency language.
// Falls back to deterministic single-repair copy when prose hasn't been generated yet.

import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { reasonCode } from './reasonCodes';
import { verdictFromLenses, STATUS_META, LENS_MEANING, BAND_STYLE } from './verdictModel';
import { InfoDot, WikiDrawer } from './InfoUI';
import VerdictSpectrum from './VerdictSpectrum';
import { ROLES, LENS_WIKI, LENS_META, wikiArticle } from './wikiContent';
import { AXIS_INFO } from './axisInfo';
import { unitDisplayLabel, unitTitle } from './unitLabels';
import { grainPlural, grainSingular } from './grainNouns';
import Thumbs, { thumbKey } from './Thumbs';

const NAVY = '#14536e';
const ROSE = '#b3261e';
const GREEN = '#2AA147';
const AMBER = '#9a6700';

import { useConnections, sendConnectionsEvent, groupByAxis, groupByCriterion, ConvergenceModule, SharedFindingsChip, renderHighlighted, criterionLabel } from './ConnectionsTab';
import CraftReadTab, { useCraftRead } from './CraftReadTab';
import { tip } from './Tip';
import PageToc, { useScrollSpy } from './PageToc';
import { fmtScore, LENSES, LENS_HEADLINE } from './Constants';   // one score formatter — no bare toFixed(1)

// LENSES moved to Constants.js 2026-09-04 — the Axes tab (Icicle.js AxesOverview) now groups by the
// same taxonomy, and two copies of it would drift.
const AXES = LENSES.flatMap(l => l.axes);
const AXIS_LABEL = {
  contract: 'Contract', pressure: 'Pressure', development: 'Development', movement: 'Movement', consequence: 'Consequence',
  economy: 'Economy', assembly: 'Assembly', clarity: 'Clarity',
  freshness: 'Freshness', unpredictability: 'Unpredictability', charge: 'Charge', grip: 'Grip',
};
const ACTION_META = {
  repair: {
    button: n => (n > 1 ? `Choose one of ${n} fixes` : 'See the fix'),
    modal: n => (n > 1 ? `Choose one of ${n} fixes` : 'The fix'),
    intro: 'Each tackles the same problem a different way; the recommended one is the cheapest that works. Open any to weigh its trade-offs.',
    fg: '#fff',
    bg: NAVY,
    bd: NAVY,
  },
  strengthen: {
    button: n => (n > 1 ? `See ${n} ways to strengthen` : 'See a way to strengthen'),
    modal: n => (n > 1 ? `Choose one of ${n} ways to strengthen` : 'Way to strengthen'),
    intro: 'These are optional lifts: useful when you want this axis to move from functional to sharper without treating it as broken.',
    fg: NAVY,
    bg: '#fff',
    bd: NAVY,
  },
  protect: {
    button: n => (n > 1 ? `Protect with ${n} options` : 'Protect this'),
    modal: n => (n > 1 ? `Choose one of ${n} ways to protect it` : 'Protect this while revising'),
    intro: 'These are guardrails for revision: ways to change the unit without damaging what already works.',
    fg: GREEN,
    bg: '#fff',
    bd: '#9bd5aa',
  },
  probe: {
    button: n => (n > 1 ? `Explore ${n} angles` : 'Explore this'),
    modal: n => (n > 1 ? `Choose one of ${n} angles` : 'Explore this'),
    intro: 'These are diagnostic probes: ways to test whether the read changes before committing to a larger revision.',
    fg: NAVY,
    bg: '#fff',
    bd: '#cbd5e1',
  },
};
const OBLIG = {
  core:     { dot: '●', label: 'core',     fg: NAVY },
  expected: { dot: '◐', label: 'expected', fg: '#64748b' },
  light:    { dot: '○', label: 'light',    fg: '#aab' },
};
// The compact D·E·R read teaches the framework by repetition: a plain-English line per
// lens with the axis as a small clickable breadcrumb. Each lens carries a stable accent
// + writer-translation so "Design / Execution / Read" become second nature.
const LENS_VERB = { design: 'what it’s built to do', execution: 'how cleanly it plays', read: 'what the reader feels' };
const LENS_ACCENT = { design: '#3b6fb0', execution: '#7c5cbf', read: '#1f9aa8' };
// LENS_HEADLINE moved to Constants.js 2026-09-04 — the Axes tab prints it too.
// one read line: ✓/✗ + plain diagnosis with the axis tag INLINE right after the sentence
// (where the eye/cursor already is — not stranded at the right edge). Clicking expands it below.
function ReadLine({ plus, gloss, ax, onPick }) {
  // Indented under the lens, AXIS NAME FIRST (clickable) so the vocabulary leads and sticks,
  // then the plain-English explanation: "✗ Development — No character change; pure setup."
  return (
    <div className="d-flex" style={{ gap: 8, marginTop: '.5rem', marginLeft: '1.25rem' }}>
      <span style={{ color: plus ? GREEN : ROSE, fontWeight: 700, flexShrink: 0, fontSize: '.95rem', lineHeight: 1.55 }}>{plus ? '✓' : '✗'}</span>
      <div style={{ color: '#334155', fontSize: '.92rem', lineHeight: 1.55 }}>
        <button type="button" onClick={() => onPick(ax)}
          {...tip(`See ${AXIS_LABEL[ax] || ax} below`)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: NAVY, fontWeight: 700, fontSize: '.92rem', padding: 0, textDecoration: 'underline', textUnderlineOffset: 2 }}>
          {AXIS_LABEL[ax] || ax}
        </button>
        {' — '}{gloss || (plus ? 'working here' : 'needs attention')}
      </div>
    </div>
  );
}
ReadLine.propTypes = { plus: PropTypes.bool, gloss: PropTypes.string, ax: PropTypes.string, onPick: PropTypes.func };
const BAND = (s) => (s == null ? null : s < 3 ? 'fail' : s < 5 ? 'weak' : s <= 6 ? 'functional' : s <= 8 ? 'strong' : 'exceptional');
const BAND_COLOR = { fail: ROSE, weak: ROSE, functional: '#b8860b', strong: GREEN, exceptional: GREEN };
// The little triangle that connects an active rail row to the spotlight card beside it. AxisRow
// draws its own inline (see `pointer`); this is the same shape for the lens headers, which are not
// AxisRows. Kept as one helper so the two never drift apart.
const pointerFor = (color) => (
  <span aria-hidden style={{ position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: `9px solid ${color}` }} />
);

// percentile of `val` among `peers` (array of numbers), as "% at or below".
function pct(val, peers) {
  const xs = peers.filter(x => typeof x === 'number' && !isNaN(x));
  if (val == null || xs.length < 4) return null;          // too few to be honest
  const below = xs.filter(x => x <= val).length;
  return Math.round((below / xs.length) * 100);
}
function ordinal(n) {
  if (n == null) return null;
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// One axis row in the scoreboard.
//
// `dense` (owner, 2026-09-04) shrinks the row so it sits UNDER its lens instead of over it. In the
// twocol rail the axis label was .9rem/500 against a .86rem/700 lens header and the axis number was
// .9rem against a .68rem lens pill — i.e. the child out-shouted its own parent on both counts, and
// the reader met twelve axes before they met the three lenses those axes belong to. Dense drops the
// label to .82rem and the number to .82rem; the lens header is simultaneously raised. Off by
// default so the `single` branch's "The three lenses" card is untouched.
function AxisRow({ axis, ax, peerScores, onPick, active, hasRepair, flagged, pointer = false, showAll = false, convCount = 0, dense = false }) {
  const rawS = ax ? (typeof ax.s === 'number' ? ax.s : parseFloat(ax.s)) : NaN;
  const notOwed = !ax || ax.st === 'na';
  // Owed axes read as before. With "show all scores" on, a not-owed axis reveals its
  // underlying raw score too (styled muted); otherwise it stays a dash.
  const reveal = showAll && notOwed && Number.isFinite(rawS);
  const s = (!notOwed && Number.isFinite(rawS)) ? rawS : (reveal ? rawS : null);
  const na = s == null;
  const o = OBLIG[ax?.o] || OBLIG.expected;
  const band = BAND(s);
  const fillW = na ? 0 : Math.max(2, (s / 10) * 100);
  const barColor = na ? '#e2e8f0' : (reveal ? '#cbd5e1' : (BAND_COLOR[band] || '#94a3b8'));
  return (
    <button
      type="button"
      onClick={() => onPick && onPick(axis)}
      className="w-100 text-start d-flex align-items-center gap-2 px-2 py-1"
      style={{ position: 'relative', border: 'none', borderLeft: `3px solid ${active ? (BAND_COLOR[band] || NAVY) : 'transparent'}`, background: active ? '#e8f1f7' : (flagged ? '#fff7f6' : 'transparent'), borderRadius: active ? '0 7px 7px 0' : 7, cursor: 'pointer' }}
    >
      <span {...tip(o.label)} style={{ color: o.fg, fontSize: '.7rem', width: 12, flexShrink: 0 }}>{o.dot}</span>
      <span {...tip(AXIS_INFO[axis]?.measures || '')} style={{ width: dense ? 104 : 118, flexShrink: 0, color: dense ? '#334155' : '#0f172a', fontWeight: (flagged || active) ? 700 : 500, fontSize: dense ? '.82rem' : '.9rem' }}>
        {AXIS_LABEL[axis] || axis}
      </span>
      <span style={{ flex: 1, height: 7, background: '#eef2f6', borderRadius: 4, position: 'relative', minWidth: 50 }}>
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${fillW}%`, background: barColor, borderRadius: 4 }} />
      </span>
      <span {...tip(reveal ? 'Not owed for this unit — shown because “Show all scores” is on' : null)} style={{ width: dense ? 26 : 30, textAlign: 'right', fontWeight: 700, fontStyle: reveal ? 'italic' : 'normal', color: na ? '#aab' : (reveal ? '#94a3b8' : (dense ? '#334155' : '#0f172a')), fontSize: dense ? '.82rem' : '.9rem' }}>
        {na ? '—' : fmtScore(s)}
      </span>
      {/* ◇ = another approved reading of a moment on this page — NOT good/bad/agreement/confidence */}
      {convCount > 0 && (
        <span {...tip('Also identified in Craft Read')} style={{ width: 20, textAlign: 'center', flexShrink: 0, color: '#8b6bb0', fontWeight: 700, fontSize: '.74rem' }}>
          ◇{convCount > 1 ? convCount : ''}
        </span>
      )}
      {active && pointer && <span aria-hidden style={{ position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: `9px solid ${BAND_COLOR[band] || NAVY}` }} />}
      <span style={{ width: 14, textAlign: 'center', color: active ? (BAND_COLOR[band] || NAVY) : (hasRepair ? NAVY : '#cbd5e1'), fontWeight: 800 }}>{active ? '▸' : '›'}</span>
    </button>
  );
}
AxisRow.propTypes = { axis: PropTypes.string, ax: PropTypes.object, peerScores: PropTypes.array, onPick: PropTypes.func, active: PropTypes.bool, hasRepair: PropTypes.bool, flagged: PropTypes.bool, dense: PropTypes.bool };

// The recommended NEXT MOVE — STATE THE PROBLEM FIRST (what's wrong + what it
// costs the read) so the writer wants a fix, THEN reveal the options on demand.
// No force-feeding the forks.
// The action MODAL — opens over the pane so action happens here (keeps the right
// pane scroll clean). Shows the paths/forks, and for a SCENE, a live "Write this
// for me" that calls the same /assistant/path-execution LLM endpoint the legacy
// scene D&E modal uses. Manages its own Escape (stops propagation so the table
// drawer's Escape doesn't also fire).
const EFFORT_PILL = { polish: 'Polish', trim_reframe: 'Trim / reframe', rewrite: 'Rewrite', cut_pickup: 'Cut & pick up', rebuild: 'Rebuild' };
const MODAL_HEADER = '#083A54'; // the app's navbar/footer teal — modal headers match it (white text)
function RepairModal({ entry, problem, problemType, sceneNumber, movieHash, movieId, onClose, readOnly = false }) {
  const paths = asList(entry.paths).sort((a, b) => (a.rank || 9) - (b.rank || 9));
  const secondaryAxes = asList(entry.secondary_axes).map(asText).filter(Boolean);
  const intent = actionIntent(entry);
  const meta = ACTION_META[intent] || ACTION_META.repair;
  // Selection mode (stamped by PHP; fall back to intent for legacy rows). one_of = exclusive
  // forks with a recommended pick; any_of = a non-exclusive menu (try any), no recommended —
  // so a lone move never reads as a command.
  const mode = entry.selection || (intent === 'repair' ? 'one_of' : 'any_of');
  const [exec, setExec] = useState({});   // letter -> { loading | text | error }
  const canWrite = (!!movieHash || !!movieId) && sceneNumber != null;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const writeThis = async (p) => {
    if (!canWrite) return;
    setExec(s => ({ ...s, [p.letter]: { loading: true } }));
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const r = await fetch('/assistant/path-execution', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(token ? { 'X-CSRF-TOKEN': token } : {}) },
        body: JSON.stringify({
          movie_hash: movieHash || '',
          scene_number: sceneNumber,
          surface: intent === 'protect' ? 'axis' : (intent === 'strengthen' ? 'push' : intent),
          parent_slug: entry.slug || entry.id,
          item_key: p.letter,
          path_data: p,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.text) setExec(s => ({ ...s, [p.letter]: { text: j.text } }));
      else setExec(s => ({ ...s, [p.letter]: { error: j.credits_short ? 'You’re out of brainstorm credits.' : (j.error || 'Couldn’t generate — try again.') } }));
    } catch (e) { setExec(s => ({ ...s, [p.letter]: { error: 'Network error — try again.' } })); }
  };
  const n = paths.length;
  // Accordion: ALL options start collapsed so the three headlines sit together and
  // are genuinely comparable at a glance; the writer expands whichever they want to
  // read the move + trade-offs + write button. Multiple can be open at once. (The
  // recommended one is still marked by its badge + border while collapsed.)
  const [open, setOpen] = useState(() => new Set());
  const toggle = (l) => setOpen(s => { const nx = new Set(s); nx.has(l) ? nx.delete(l) : nx.add(l); return nx; });
  return (
    <div data-sos-modal onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 16px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        onClickCapture={() => { try { window.srTrack?.('sos_modal_click', 'pcr', { number: sceneNumber, unit_type: 'scene' }); } catch (e) {} }}
        style={{ background: '#fff', borderRadius: 14, maxWidth: 680, width: '100%', boxShadow: '0 24px 70px rgba(15,23,42,.35)', overflow: 'hidden' }}>
        {/* Dark teal header (app modal convention) — the action's goal + the axis */}
        <div style={{ background: MODAL_HEADER, color: '#fff', padding: '.95rem 1.4rem', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.25 }}>{entry.label}</div>
            <div style={{ color: '#bcd7e6', fontSize: '.82rem', marginTop: '.15rem' }}>
              {AXIS_LABEL[entry.primary_axis] || entry.primary_axis}
              {secondaryAxes.length > 0 && <> · also lifts {secondaryAxes.map(a => AXIS_LABEL[a] || a).join(', ')}</>}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ border: 'none', background: 'transparent', color: '#cfe3ee', fontSize: '1.35rem', lineHeight: 1, cursor: 'pointer', padding: '.1rem .3rem' }}>✕</button>
        </div>
        <div style={{ padding: '1.1rem 1.4rem' }}>
          {/* Problem TYPE first (reason-code hook), then the prose elaboration */}
          {problemType && <div style={{ color: ROSE, fontWeight: 700, fontSize: '1.02rem', lineHeight: 1.3 }}>{problemType}</div>}
          {problem && <div style={{ color: '#475569', fontSize: '.92rem', lineHeight: 1.5, margin: `${problemType ? '.3rem' : '0'} 0 1.1rem` }}>{problem}</div>}
          {/* The decision — stated plainly so it's obvious a choice is asked */}
          <div style={{ fontWeight: 700, fontSize: '.95rem', color: NAVY, marginBottom: '.15rem' }}>
            {mode === 'one_of'
              ? meta.modal(Math.max(1, n))
              : (n > 1
                  ? `${n} ${intent === 'protect' ? 'ways to protect this' : 'optional lifts'} — try any`
                  : (intent === 'protect' ? 'One way to protect this, if you want it' : 'One optional lift, if you want it'))}
            {canWrite ? ' — I’ll draft it for you' : ''}
          </div>
          {(mode === 'any_of' || n > 1) && (
            <div style={{ color: '#64748b', fontSize: '.84rem', lineHeight: 1.45, marginBottom: '.7rem' }}>
              {mode === 'any_of'
                ? (intent === 'protect'
                    ? 'Guardrails for the rewrite — any of these help; they’re not a to-do list.'
                    : 'Optional lifts — take any that appeal; they stack, and none is required.')
                : meta.intro}
            </div>
          )}
          {paths.map((p, idx) => {
            const letter = p.letter || String.fromCharCode(65 + idx);
            const rec = mode === 'one_of' && (p.recommended || p.is_recommended || p.is_lean);
            const ex = exec[letter] || {};
            const isOpen = open.has(letter);
            const pathForWrite = { ...p, letter };
            return (
              <div key={letter} className="mb-2" style={{ border: `${rec ? 2 : 1}px solid ${rec ? NAVY : '#cbd5e1'}`, borderRadius: 10, background: rec ? '#f7fbfd' : '#fff', boxShadow: rec ? 'none' : '0 1px 2px rgba(15,23,42,.05)', overflow: 'hidden' }}>
                {/* Collapsed header (always visible): headline + the description, so the
                    options are comparable without expanding. Expanding adds the trade-offs. */}
                <button type="button" onClick={() => toggle(letter)} aria-expanded={isOpen}
                  className="w-100"
                  style={{ display: 'block', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', padding: '.6rem .8rem' }}>
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <span style={{ color: NAVY, fontSize: '.8rem', width: 12 }}>{isOpen ? '▾' : '▸'}</span>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{p.headline || p.subline || `Path ${p.letter}`}</span>
                    {rec && <span style={{ fontSize: '.66rem', fontWeight: 800, letterSpacing: '.04em', color: '#fff', background: NAVY, borderRadius: 999, padding: '.08rem .5rem' }}>★ RECOMMENDED</span>}
                    {p.effort && <span style={{ fontSize: '.7rem', color: '#64748b', marginLeft: 'auto' }}>{EFFORT_PILL[p.effort] || p.effort}</span>}
                  </div>
                  {p.move && <div style={{ color: '#334155', fontSize: '.88rem', lineHeight: 1.5, marginTop: '.3rem', paddingLeft: '1.05rem' }}>{p.move}</div>}
                </button>
                {isOpen && (() => {
                  const re = p.reader_experience || {};
                  const gain = asList(re.gain);
                  const cost = asList(re.cost);
                  const hasTrade = gain.length > 0 || cost.length > 0;
                  return (
                    <div style={{ padding: '0 .8rem .75rem 1.85rem' }}>
                      {hasTrade && (
                        // Trade-offs grouped in one tinted block — gains then costs, tight rows,
                        // set apart from the description above and the button below.
                        <div style={{ padding: '.5rem .7rem', background: '#f8fafc', border: '1px solid #eef2f6', borderRadius: 8, marginTop: '.2rem' }}>
                          {gain.map((g, i) => <div key={'g' + i} style={{ color: GREEN, fontSize: '.83rem', lineHeight: 1.45, marginBottom: 3 }}>+ {asText(g)}</div>)}
                          {cost.map((c, i) => <div key={'c' + i} style={{ color: ROSE, fontSize: '.83rem', lineHeight: 1.45, marginBottom: 3 }}>− {asText(c)}</div>)}
                        </div>
                      )}
                      {canWrite && (
                        <div style={{ marginTop: '.65rem' }}>
                          <button type="button" onClick={() => writeThis(pathForWrite)} disabled={ex.loading}
                            style={{ fontWeight: 600, fontSize: '.84rem', color: ex.loading ? '#94a3b8' : NAVY, background: '#fff', border: `1.5px solid ${ex.loading ? '#cbd5e1' : NAVY}`, borderRadius: 999, padding: '.3rem .9rem', cursor: ex.loading ? 'default' : 'pointer' }}>
                            {ex.loading ? 'Writing…' : (ex.text ? '✍️ Draft again' : (intent === 'repair' ? '✍️ Write this for me' : '✍️ Draft this move'))}
                          </button>
                          {ex.text && <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '.8rem', color: '#0f172a', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginTop: '.5rem', lineHeight: 1.5 }}>{ex.text}</pre>}
                          {ex.error && <div style={{ color: ROSE, fontSize: '.82rem', marginTop: '.4rem' }}>{ex.error}</div>}
                        </div>
                      )}
                      {readOnly && !canWrite && (
                        /* ⚠️ READS AS A PREVIEW, NOT A LOCK (owner, 2026-09-23: "I like the teaser,
                           so people understand what can be"). The disabled button on its own said
                           "you can't"; the badge + the line below say "here is what this does on
                           your own script", which is the persuasive half. readOnly-only — the paid
                           app never renders this branch. */
                        <div style={{ marginTop: '.65rem' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                            <button type="button" disabled aria-hidden="true"
                              style={{ fontWeight: 600, fontSize: '.84rem', color: '#94a3b8', background: '#f8fafc', border: '1.5px dashed #cbd5e1', borderRadius: 999, padding: '.3rem .9rem', cursor: 'default' }}>
                              {intent === 'repair' ? '✍️ Write this for me' : '✍️ Draft this move'}
                            </button>
                            <span style={{ fontWeight: 700, fontSize: '.66rem', color: NAVY, background: '#dbe9f2', borderRadius: 999, padding: '.1rem .45rem' }}>preview</span>
                          </div>
                          <div style={{ color: '#5b7481', fontSize: '.78rem', marginTop: '.35rem', lineHeight: 1.5 }}>On your own analysis this button drafts the fix, in your scene, in your voice.</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
RepairModal.propTypes = {
  entry: PropTypes.object,
  problem: PropTypes.string,
  problemType: PropTypes.string,
  sceneNumber: PropTypes.number,
  movieHash: PropTypes.string,
  movieId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
};

const SectionLabel = ({ children }) => (
  <div style={{ color: NAVY, fontWeight: 600, fontSize: '1rem', margin: '1.4rem 0 .5rem' }}>{children}</div>
);
SectionLabel.propTypes = { children: PropTypes.node };

const STATUS_PILL = {
  primary_problem:    { label: 'Primary problem',   fg: ROSE,  bg: '#fff5f4', bd: '#f0cdc9' },
  secondary_problem:  { label: 'Secondary',         fg: AMBER, bg: '#fff7e6', bd: '#f0d8a8' },
  working_strength:   { label: 'Working',           fg: GREEN, bg: '#f0faf2', bd: '#bfe6c8' },
  protected_strength: { label: 'Protect',           fg: GREEN, bg: '#f0faf2', bd: '#bfe6c8' },
  lightly_relevant:   { label: 'Light',             fg: '#64748b', bg: '#f1f5f9', bd: '#cbd5e1' },
  not_owed:           { label: 'Not owed',          fg: '#94a3b8', bg: '#f8fafc', bd: '#e2e8f0' },
  mixed:              { label: 'Mixed',             fg: AMBER, bg: '#fff7e6', bd: '#f0d8a8' },
};

function asList(value) {
  if (value == null || value === false) return [];
  if (Array.isArray(value)) return value.flatMap(asList);
  if (typeof value === 'string' || typeof value === 'number') return [{ move: String(value) }];
  if (typeof value !== 'object') return [];
  if (value.move || value.label || value.question || value.text || value.summary || value.tradeoff || value.rank || value.letter) return [value];
  return Object.values(value).flatMap(asList);
}

function asText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value.move || value.question || value.text || value.label || value.summary || '';
}

function actionIntent(entry) {
  const raw = String(entry?.intent || entry?.action || entry?.kind || entry?.type || '').toLowerCase();
  if (['push', 'strengthen', 'strengthening', 'lift'].includes(raw)) return 'strengthen';
  if (['repair', 'protect', 'probe'].includes(raw)) return raw;
  return 'repair';
}

function actionButtonText(entry) {
  const intent = actionIntent(entry);
  const n = asList(entry?.paths).length;
  return (ACTION_META[intent] || ACTION_META.repair).button(Math.max(1, n));
}

function actionButtonStyle(entry) {
  const meta = ACTION_META[actionIntent(entry)] || ACTION_META.repair;
  return {
    fontWeight: 600,
    fontSize: '.88rem',
    color: meta.fg,
    background: meta.bg,
    border: `1.5px solid ${meta.bd}`,
    borderRadius: 999,
    padding: '.35rem 1.05rem',
    cursor: 'pointer',
  };
}

function actionDiagnosis(entry, fallback = '') {
  const problem = asText(entry?.problem);
  const readerCost = asText(entry?.reader_cost);
  const parts = [problem, readerCost && readerCost !== problem ? readerCost : ''].filter(Boolean);
  return parts.join(' ') || fallback || '';
}

function shortActionHeadline(text, fallback) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return fallback;
  const firstClause = clean.split(/[.;:!?]/)[0].trim();
  const words = firstClause.split(/\s+/).slice(0, 5).join(' ');
  return words || fallback;
}

function suggestionPath(s, i, intent, axis) {
  const letter = String.fromCharCode(65 + i);
  const move = asText(s);
  const trade = s?.tradeoff || {};
  const gain = trade.gain || s?.gain || (intent === 'protect' ? 'Keeps the current strength intact.' : '');
  const cost = trade.cost || s?.cost || '';
  return {
    letter,
    type: s?.type || intent,
    recommended: i === 0 || !!s?.recommended || !!s?.is_recommended || !!s?.is_lean,
    is_recommended: i === 0 || !!s?.recommended || !!s?.is_recommended || !!s?.is_lean,
    headline: s?.headline || s?.label || shortActionHeadline(move, intent === 'repair' ? `Option ${letter}` : ACTION_META[intent].modal(1)),
    subline: s?.subline || '',
    move,
    reader_experience: { gain: gain ? [gain] : [], cost: cost ? [cost] : [] },
    effort: s?.effort || null,
    axis_impact: s?.axis_impact || { primary_lift: [axis], secondary_lift: [], risk: [] },
    projected_verdict: s?.projected_verdict || null,
  };
}

function normalizeTopLevelAction(entry, intent, i) {
  if (!entry || typeof entry !== 'object') return null;
  const normalizedIntent = actionIntent({ ...entry, intent });
  const primary = entry.primary_axis || entry.axis;
  if (!primary) return null;
  if (asList(entry.paths).length > 0) {
    return {
      ...entry,
      id: entry.id || entry.slug,
      slug: entry.slug || entry.id,
      intent: normalizedIntent,
      problem: entry.problem || entry.reader_cost || entry.when_attractive || entry.effect || entry.do_not_break || '',
      primary_axis: primary,
    };
  }
  const sourcePaths = normalizedIntent === 'protect'
    ? asList(entry.safe_revision_moves || entry.paths || entry.moves)
    : asList(entry.paths || entry.moves || entry);
  const paths = sourcePaths.map((p, pi) => suggestionPath(p, pi, normalizedIntent, primary)).filter(p => p.move);
  if (!paths.length && entry.move) paths.push(suggestionPath(entry, 0, normalizedIntent, primary));
  if (!paths.length) return null;
  return {
    ...entry,
    intent: normalizedIntent,
    id: entry.id || entry.slug || `${normalizedIntent}-${primary}-${i + 1}`,
    slug: entry.slug || entry.id || `${normalizedIntent}-${primary}-${i + 1}`,
    label: entry.label || `${ACTION_META[normalizedIntent].modal(1)}: ${AXIS_LABEL[primary] || primary}`,
    problem: entry.problem || entry.when_attractive || entry.effect || entry.do_not_break || '',
    primary_axis: primary,
    secondary_axes: entry.secondary_axes || [],
    rank: entry.rank || (normalizedIntent === 'repair' ? i + 1 : 20 + i),
    recommended_letter: entry.recommended_letter || 'A',
    paths,
  };
}

function fallbackAction(axis, intent, suggestions, card, node, rankOffset = 0) {
  const paths = asList(suggestions).map((s, i) => suggestionPath(s, i, intent, axis)).filter(p => p.move);
  if (!paths.length) return null;
  const axisName = AXIS_LABEL[axis] || axis;
  const label = intent === 'repair'
    ? `Repair ${axisName}`
    : intent === 'strengthen'
      ? `Strengthen ${axisName}`
      : `Protect ${axisName}`;
  return {
    slug: `axis-${axis}-${intent}`,
    label,
    intent,
    problem: card?.explanation || node?.headline || '',
    primary_axis: axis,
    secondary_axes: [],
    rank: intent === 'repair' ? 10 + rankOffset : 30 + rankOffset,
    recommended_letter: 'A',
    paths,
  };
}

// ⚠️ MODULE SCOPE, NOT COMPONENT STATE, AND DELIBERATELY NOT PERSISTED (owner, 2026-08-25).
// Which AI questions have already been sent must survive NodePanel unmounting — the panel is torn
// down and rebuilt every time the reader moves to another unit, so component state would forget an
// asked question the moment they clicked away and came back. A module-level Set lives exactly as
// long as the page does, which is the requested lifetime: "in the session at least, doesn't have to
// be persisted." A reload starts clean, which is right — the answers are gone from the chat too.
// Keyed by node + axis + question so the same question text on a different unit is still askable.
const ASKED_QUESTIONS = new Set();
const askedKey = (node, axis, q) => `${node?.id ?? node?.uid ?? node?.label ?? '?'}|${axis}|${q}`;

export default function NodePanel({ node, peers, movieHash, movieId, focusBlock = 'nearest', layout = 'single', onDiscussAxis = null, onFocusAct = null, readOnly = false, showAllScoresDefault = false, craftHash = null, script = null, measure = false, toc = false }) {
  // ⚠️ `craftHash` is SEPARATE from `movieHash` on purpose. `canWrite` is derived from movieHash,
  // and the snapshot deliberately passes an EMPTY movieHash to viewers so the generate/repair
  // button never renders. Craft Read still needs an identifier for those same viewers, so it gets
  // its own prop — reusing movieHash here would silently hand non-owners the write affordance.
  // Bumped on every send purely to force a re-render — the Set above is the source of truth.
  const [, bumpAsked] = useState(0);
  // ⚠️ TWO-COLUMN ONLY IF THE PANEL IS ACTUALLY WIDE ENOUGH — measured on ITSELF, not on the window.
  // `grades.css` stacks `.np-twocol` at `@media (max-width: 700px)`, which is a VIEWPORT query and
  // therefore cannot fire when the panel is narrow inside a wide window. That is the common case now:
  // at a 1000px window with the chat overlaying, the reading card is 544px and the axis spotlight was
  // measured at 276px — a column of wrapped words — while the media query sat happily unfired at
  // 1000px. Same for the List view's side-by-side split, and for any future layout that narrows the
  // pane. 640 is where rail (210 floor) + gap + padding still leaves ~370 of readable prose.
  const rootRef = useRef(null);
  const [selfW, setSelfW] = useState(0);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setSelfW(el.clientWidth));
    ro.observe(el); setSelfW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const NP_TWOCOL_MIN = 640;
  // "ON THIS READING" TOC (owner, 2026-10-14: "toc could also be added to all the units — helps people
  // understand what's on the page"). Shown beside the twocol diagnosis when the host asks for it (`toc`)
  // and there is room: the wrapper is measured, and the reading itself keeps ≥ ~800px.
  const wrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!toc || !el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWrapW(el.clientWidth));
    ro.observe(el); setWrapW(el.clientWidth);
    return () => ro.disconnect();
  });   // eslint-disable-line react-hooks/exhaustive-deps -- re-attach whenever the wrapper remounts
  const NP_TOC_IDS = ['np-summary', 'np-fix', 'np-read', 'np-happens', 'np-axes', 'np-lens-design', 'np-lens-execution', 'np-lens-read'];
  const [tocActive] = useScrollSpy(NP_TOC_IDS, wrapRef);
  const twocol = layout === 'twocol' && (selfW === 0 || selfW >= NP_TWOCOL_MIN);
  const prose = node?.prose || null;
  const axes = node?.scores || null;            // {axisKey:{score,obligation,status}, design:{score},...}
  const spotRef = useRef(null);
  // The demo-mode "these are switched off" line, and its click acknowledgement. A press on an
  // inert Ask-the-AI control lights this for a beat and scrolls it into view — that is the whole
  // affordance, in place of a tooltip nobody on a phone could reach (CLAUDE.md UI rule 7).
  const askNoteRef = useRef(null);
  const [askNoteLit, setAskNoteLit] = useState(false);
  const askNoteTimer = useRef(null);
  const flashAskNote = useCallback(() => {
    setAskNoteLit(true);
    if (askNoteTimer.current) clearTimeout(askNoteTimer.current);
    askNoteTimer.current = setTimeout(() => setAskNoteLit(false), 1400);
    const el = askNoteRef.current;
    // Nearest scroller only — never the window. Same rule as the spotlight effect below
    // (landmine 184): scrollIntoView() would move the modal AND the page.
    if (el) {
      let sc = el.parentElement;
      while (sc && sc !== document.body && sc !== document.documentElement) {
        const oy = getComputedStyle(sc).overflowY;
        if (/(auto|scroll|overlay)/.test(oy) && sc.scrollHeight > sc.clientHeight + 1) break;
        sc = sc.parentElement;
      }
      if (sc && sc !== document.body && sc !== document.documentElement) {
        const er = el.getBoundingClientRect(), sr = sc.getBoundingClientRect();
        if (er.bottom > sr.bottom || er.top < sr.top) sc.scrollTop += (er.top - sr.top) - 12;
      }
    }
  }, []);
  useEffect(() => () => { if (askNoteTimer.current) clearTimeout(askNoteTimer.current); }, []);
  // The axis whose detail is spotlighted. Seeded by the table click (node.focusAxis),
  // then steerable by clicking any axis row in the in-panel grid (so all 12 are clickable).
  const [localFocus, setLocalFocus] = useState(null);
  const [tab, setTab] = useState('diagnosis');   // 'diagnosis' | 'text'
  // Connections (ship plan D3, #99): server-gated by the per-account allowlist —
  // a non-allowlisted account gets {enabled:false} and this stays null, so the
  // feature does not exist rather than existing empty. Scene nodes only.
  // Scene number lives at node.number for scene nodes (same derivation the
  // RepairModal uses below) — NOT node.scene_number, which does not exist here.
  // REDESIGN 2026-08-12: no Connections tab. Convergence renders INSIDE the
  // axis spotlight (after the axis's own evidence — corroboration, not badge),
  // as ◇ marks on connected rail rows, and as a "shared findings" header chip.
  const connectionsPairings = useConnections(movieHash, node?.type === 'scene' ? node.number : null);
  const connectionsByAxis = useMemo(() => groupByAxis(connectionsPairings), [connectionsPairings]);
  const [hiddenConn, setHiddenConn] = useState(() => new Set());       // #108 hide, per approved_key
  const hideConn = (key) => setHiddenConn(s => { const n = new Set(s); n.add(key); return n; });
  const connCount = (axis) => (connectionsByAxis[axis] || []).filter(g => !hiddenConn.has(g.pairing.approval.approved_key)).length;
  const [connHighlight, setConnHighlight] = useState(null);            // "Show me on the page" target quote
  const showOnPage = (quote) => { setConnHighlight(quote); setTab('text'); };
  useEffect(() => {
    if (tab === 'text' && connHighlight) {
      const el = document.getElementById('conn-highlight');
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [tab, connHighlight]);
  // CRAFT READ (redesign 2026-08-12): the traditional criteria as a sibling tab —
  // same admin gate ({enabled:false} → the tab does not exist). The reciprocal
  // convergence modules in both instrument views jump to each other.
  // `movieHash` is the snapshot's only identifier — it is never given the internal numeric id —
  // so pass both and let the hook pick its endpoint.
  const craftData = useCraftRead(movieId, node?.type === 'scene' ? node.number : null, craftHash || movieHash || null);
  const craftCriteria = craftData?.criteria?.length ? craftData.criteria : (craftData ? [] : null);
  const craftKeys = useMemo(() => new Set((craftCriteria || []).map(c => c.key)), [craftCriteria]);
  const [craftFocus, setCraftFocus] = useState(null);
  const connByCriterion = useMemo(() => groupByCriterion(connectionsPairings), [connectionsPairings]);
  const openCriterion = (k) => { setCraftFocus(k); setTab('craftread'); };
  // Craft Read → the docked AI chat, reusing the SAME path a DER axis uses (`onDiscussAxis`), so a
  // criterion question and an axis question land in one chat with one thread model. The criterion
  // key is not a DER axis, hence the explicit label — see `discussAxis`'s `labelOverride`.
  // `question === false` means "ask your own": open the chat blank, nothing auto-sent.
  const askCriterion = onDiscussAxis
    ? ((critKey, critLabel, question) => onDiscussAxis(critKey, question, critLabel))
    : null;
  const openAxis = (ax) => { setLocalFocus(ax); setTab('diagnosis'); };
  const [sumOpen, setSumOpen] = useState(false);  // "What happens" recap: collapsed to 2 lines by default
  const [modal, setModal] = useState(null);             // {entry, problem} → the action modal
  const [wiki, setWiki] = useState(null);               // open WikiDrawer article (role / band / lens / overview)
  const [openPill, setOpenPill] = useState(null);
  const [scrollReq, setScrollReq] = useState(0);         // bumped by an explicit "Go to axes"
  const lastScrollReq = useRef(0);
  const goToAxis = (ax) => { setLocalFocus(ax); setScrollReq(n => n + 1); };        // which axis pill (single-column verdict strip) is expanded
  // C8-SAFE default: the 12-axis RAIL/SPOTLIGHT (`setLocalFocus` below) still opens NEUTRAL — no
  // axis auto-focuses there on a fresh unit. The old logic pre-selected the lowest RAW score among
  // owed axes, but axis raw scores aren't on a common scale (clarity sits well above every film's
  // mean, freshness below), so it systematically pre-blamed freshness/development on nearly every
  // script. The C8-safe *script* priority axis is surfaced on the Axes tab, not re-derived per unit
  // from raw scores.
  //
  // The axis-pills fieldset (`openPill`, below) is a DIFFERENT surface added 2026-09-21 and is
  // exempt from "opens neutral": it now defaults to the worst-PERCENTILE flagged axis (owner call).
  // This is not the banned heuristic — percentile is each axis's score against its OWN peer
  // distribution, so it's comparable across axes, unlike raw score. See `problemAxes` below.
  useEffect(() => {
    setLocalFocus(node?.focusAxis || node?.targetAxis || null);   // only an explicit click focuses an axis
    // KEEP THE READER'S TAB ACROSS UNITS (owner, 2026-08-21). Clicking scene after scene while
    // comparing Craft Reads used to bounce back to Mechanics every time. The tab is kept when it
    // still MEANS something on the new unit, and falls back to Mechanics when it does not:
    //   craftread — scene-only. Craft Read is a per-scene instrument (`useCraftRead` is passed a
    //               scene number and nothing else), so any sequence/act/script click must fall back.
    //               Tested on `node.type`, which is known synchronously — `craftCriteria` is NOT,
    //               because it arrives from a fetch, and gating on it would bounce to Mechanics on
    //               every scene change and then flip back.
    //   text      — kept whenever the new unit has text to read; `readText` is derived from `node`.
    // Updater form so this does not need `tab` in the dep list (which would re-run it on every
    // tab click and defeat the whole thing).
    setTab(prev => {
      if (prev === 'craftread') return node?.type === 'scene' ? 'craftread' : 'diagnosis';
      if (prev === 'text')      return readText ? 'text' : 'diagnosis';
      return 'diagnosis';
    });
    setConnHighlight(null);   // stale highlight must not follow the reader to another unit
    setCraftFocus(null);
    setSumOpen(false);   // new unit → recap starts collapsed again
    // Default-open the first (worst-percentile) flagged axis (owner call, 2026-09-21) — see the
    // percentile note on problemAxes above for why this isn't the raw-score heuristic C8-SAFE
    // banned above. `problemAxes` reflects the NEW node already: useMemo runs during render,
    // before this effect fires.
    setOpenPill(problemAxes[0] || null);
  }, [node]);   // eslint-disable-line react-hooks/exhaustive-deps

  // "Show all scores" — reveal raw scores even on axes not owed for this unit.
  const [showAllScores, setShowAllScores] = useState(!!showAllScoresDefault);
  useEffect(() => { setShowAllScores(!!showAllScoresDefault); }, [showAllScoresDefault]);
  const hasNotOwed = useMemo(() => !!axes && AXES.some(k => axes?.[k]?.status === 'na'), [axes]);
  const showAllToggle = hasNotOwed ? (
    <label className="d-inline-flex align-items-center" style={{ gap: 6, cursor: 'pointer', color: '#64748b', fontSize: '.78rem', fontWeight: 600, margin: '.2rem 0 .5rem' }} {...tip('Reveal the raw score on axes that aren\'t owed for this unit (shown muted).')}>
      <input type="checkbox" checked={showAllScores} onChange={e => setShowAllScores(e.target.checked)} style={{ cursor: 'pointer' }} />
      Show all scores (incl. not owed)
    </label>
  ) : null;

  // peer score arrays per axis/lens, for the within-script percentile.
  const peerScores = useMemo(() => {
    const map = {};
    (peers || []).forEach(p => {
      const sc = p || {};
      Object.keys(sc).forEach(k => { (map[k] = map[k] || []).push(sc[k]?.score != null ? parseFloat(sc[k].score) : null); });
    });
    return map;
  }, [peers]);

  // axis -> actionable entries. Canonical actions[] + action_refs wins; legacy
  // repair/push/protect arrays and per-axis suggestions still normalize so old rows render.
  const actionCollections = useMemo(() => {
    const byAxis = {};
    const allRepairs = [];
    const byId = {};
    const add = (entry) => {
      if (!entry || !entry.primary_axis || asList(entry.paths).length === 0) return;
      const intent = actionIntent(entry);
      const normalized = { ...entry, intent };
      const id = normalized.id || normalized.slug;
      if (id) byId[id] = normalized;
      (byAxis[normalized.primary_axis] = byAxis[normalized.primary_axis] || []).push(normalized);
      if (intent === 'repair') {
        allRepairs.push(normalized);
      }
    };

    const canonical = asList(prose?.actions)
      .map((entry, i) => normalizeTopLevelAction(entry, actionIntent(entry), i))
      .filter(Boolean);

    if (canonical.length > 0) {
      canonical.forEach(add);
      AXES.forEach((axis) => {
        const refs = asList(prose?.axes?.[axis]?.action_refs).map(asText).filter(Boolean);
        if (!refs.length) return;
        const fromRefs = refs.map(ref => byId[ref]).filter(Boolean);
        if (fromRefs.length > 0) byAxis[axis] = fromRefs;
      });
    } else {
      asList(prose?.repair).forEach((entry, i) => add(normalizeTopLevelAction(entry, 'repair', i)));
      asList(prose?.push).forEach((entry, i) => add(normalizeTopLevelAction(entry, 'strengthen', i)));
      asList(prose?.protect).forEach((entry, i) => add(normalizeTopLevelAction(entry, 'protect', i)));
    }

    AXES.forEach((axis, axisIndex) => {
      const card = prose?.axes?.[axis];
      if (!card) return;
      const ax = axes?.[axis] || {};
      const notOwed = ax.status === 'na' || ['light', 'not_owed', 'optional'].includes(ax.obligation);
      if (notOwed) return;
      const suggestions = card.suggestions || {};
      const hasIntent = (intent) => (byAxis[axis] || []).some(entry => actionIntent(entry) === intent);
      if (!hasIntent('repair')) add(fallbackAction(axis, 'repair', suggestions.repair, card, node, axisIndex));
      if (!hasIntent('strengthen')) add(fallbackAction(axis, 'strengthen', suggestions.push, card, node, axisIndex));
      if (!hasIntent('protect')) add(fallbackAction(axis, 'protect', suggestions.protect, card, node, axisIndex));
    });

    const order = { repair: 0, strengthen: 1, protect: 2, probe: 3 };
    Object.values(byAxis).forEach(arr => arr.sort((a, b) => {
      const intentOrder = (order[actionIntent(a)] ?? 9) - (order[actionIntent(b)] ?? 9);
      return intentOrder || ((a.rank || 99) - (b.rank || 99));
    }));
    allRepairs.sort((a, b) => (a.rank || 99) - (b.rank || 99));
    return { byAxis, repairs: allRepairs };
  }, [prose, axes, node]);
  const actionsByAxis = actionCollections.byAxis;
  const allRepairActions = actionCollections.repairs;
  // The single highest-ranked repair action, if any — used only to badge which axis pill is the
  // recommended starting point. Independent of which pill opens by default (see problemAxes'
  // percentile ranking below) — "worst" and "what to fix first" can be different axes.
  const topRepair = allRepairActions[0] || null;
  // AXIS PILLS source: prose.axes[ax].status (primary_problem / secondary_problem), NOT
  // verdict.driver_axis/related_axes. Those verdict fields are a curated subset the model
  // wrote for the one-line reason — measured 2026-09-21 (docs/DER_FINDINGS_AND_IDEAS.md):
  // a unit can have 5 axes flagged with problem status while the verdict names only 3, so
  // sourcing pills from the verdict fields silently hides real flagged axes.
  const problemAxes = useMemo(() => {
    const pa = prose?.axes || {};
    const list = Object.keys(pa).filter(ax => ['primary_problem', 'secondary_problem'].includes(pa[ax]?.status));
    // Rank ties by within-script PERCENTILE (owner call, 2026-09-21), not raw score: axes aren't
    // on a common scale — the exact C8-SAFE problem (clarity trends high, freshness trends low),
    // so comparing raw scores across different axes silently favours whichever axis runs low
    // corpus-wide. `pct()` (this axis's score vs this axis's OWN peer distribution) IS comparable
    // across axes. Falls back to raw score only when there aren't enough peers for an honest
    // percentile (pct() returns null under 4 peers) — happens uniformly for every axis on a given
    // node (same peer set), so a fallback never gets mixed with a percentile in the same sort.
    const rankKey = (ax) => {
      const sc = axes?.[ax]?.score != null ? parseFloat(axes[ax].score) : null;
      const p = pct(sc, peerScores[ax] || []);
      return p != null ? p : (sc != null ? sc : 99);
    };
    return list.sort((a, b) => {
      const rank = (ax) => (pa[ax].status === 'primary_problem' ? 0 : 1);
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      return rankKey(a) - rankKey(b);
    });
  }, [prose, axes, peerScores]);
  // Cap the pill row — a scene flagged on 7 of 12 axes is unreadable as 7 equal-weight pills
  // (owner, 2026-09-21). All primary_problem axes always show (rarely more than 2-3); remaining
  // slots fill with the worst-percentile secondary_problem axes; the rest collapse to a text note
  // instead of more tabs, since the full breakdown already exists further down this same panel.
  const PILL_CAP = 4;
  const shownAxes = problemAxes.slice(0, PILL_CAP);
  const hiddenAxes = problemAxes.slice(PILL_CAP);

  // Which node this panel last scrolled for. Used to tell "the panel just opened" (node changed)
  // apart from "the reader clicked an axis" (same node, new focus) — see the effect below.
  const scrolledForNode = useRef(null);

  useEffect(() => {
    // ⚠️ DO NOT PUT scrollIntoView() BACK HERE. It scrolls EVERY scrollable ancestor, not just the
    // panel — which produced two owner-reported bugs on 2026-08-30:
    //   1. LP snapshot: opening a unit left `.lrs-modal` at scrollTop ~168, so the modal's header
    //      (unit name, band, scores) was above the fold and the reader had to scroll UP to see what
    //      they had just clicked.
    //   2. Analysis page: clicking the left rail scrolled the WINDOW down, because the window is
    //      also an ancestor.
    // Both are the same one-line cause. The replacement scrolls the nearest real scroller and
    // nothing above it.
    //
    // Two rules, and both are needed:
    //   • Never scroll on OPEN. `localFocus` is seeded from the suggested axis, so the effect fired
    //     the moment a unit was clicked and moved the panel before the reader had read anything. A
    //     freshly opened panel belongs at its top.
    //   • Never walk past a scroller to the document. If the spotlight has no scrollable ancestor
    //     inside the panel, doing nothing is correct — moving the page is what bug 2 was.
    if (!localFocus || !spotRef.current) return undefined;

    const justOpened = scrolledForNode.current !== node;
    scrolledForNode.current = node;
    const forced = scrollReq !== lastScrollReq.current;   // explicit "Go to axes" always scrolls
    lastScrollReq.current = scrollReq;
    // ...EXCEPT when the open itself asked for an axis (`targetAxis`: a Table score cell, or a map cell
    // while coloured by that axis). Then the reader clicked THAT axis, and landing on the top of the
    // reading makes them hunt for it (owner, 2026-10-15: "if I click on a score it should scroll down to
    // that axis"). localFocus is only ever seeded from an explicit click now, so this cannot revive the
    // old scroll-on-every-open bug.
    if (justOpened && !forced && !node?.targetAxis) return undefined;

    // instant, not smooth: programmatic smooth scrolling is a no-op inside some nested flex/overflow
    // scrollers (e.g. the icicle table drawer), which would leave the panel un-scrolled entirely.
    const t = setTimeout(() => {
      const el = spotRef.current;
      if (!el) return;

      let sc = el.parentElement;
      while (sc && sc !== document.body && sc !== document.documentElement) {
        const oy = getComputedStyle(sc).overflowY;
        if (/(auto|scroll|overlay)/.test(oy) && sc.scrollHeight > sc.clientHeight + 1) break;
        sc = sc.parentElement;
      }
      if (!sc || sc === document.body || sc === document.documentElement) return;

      const er = el.getBoundingClientRect();
      const sr = sc.getBoundingClientRect();
      const block = twocol ? 'nearest' : focusBlock;

      // 'nearest' leaves an already-visible spotlight alone, so a rail click does not yank a
      // perfectly readable pane to the top.
      if (block === 'nearest' && er.top >= sr.top && er.bottom <= sr.bottom) return;

      const delta = block === 'center'
        ? (er.top - sr.top) - (sr.height - er.height) / 2
        : (er.top - sr.top) - 8;   // 8px so the top border is not flush against the edge
      sc.scrollTop += delta;
    }, 100);
    return () => clearTimeout(t);
  }, [localFocus, node, focusBlock, scrollReq]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!node) return null;

  const adapt = (k) => { const v = axes?.[k]?.score; return v == null ? null : parseFloat(v); };
  const lensScore = { design: adapt('design'), execution: adapt('execution'), read: adapt('read') };
  // Same weight-free gate model as the script header, at THIS grain — so every
  // drill-down answers "what do I work on here?" the same way (band + statuses).
  const grain = node.type === 'movie' ? 'script' : node.type;
  const gv = verdictFromLenses(lensScore, grain);
  const verdict = prose?.verdict || {};

  // The per-axis spotlight — the FULL per-axis view. Rendered INLINE directly
  // below the clicked axis in the scoreboard (not up top), so the detail appears
  // where the eye already is.
  const axisSpotlight = (focus) => {
    const a = axes?.[focus] || {};
    const sc = a.status === 'na' ? null : (a.score != null ? parseFloat(a.score) : null);
    const band = BAND(sc);
    const card = prose?.axes?.[focus] || {};
    // axis-scoped only — do NOT fall back to node.headline / a unit-level summary, or every axis's
    // spotlight repeats the same scene sentence (the "identical line on every axis" bug).
    //
    // Third fallback added 2026-08-14: the writer pass sometimes fills the ACTION's `problem` /
    // `reader_cost` for an axis and leaves `axes[axis].explanation` empty. When that happened the
    // pane fell through to '' and showed only the reason code — whose label and test are STATIC
    // catalog constants (der_reason_codes.php), identical on every unit that fires them. So the
    // pane read generic while the scene-specific writing sat one click away inside the fixes modal,
    // which reads exactly these fields via actionDiagnosis(). Borrowing them keeps the axis scope
    // (actions are already per-axis) and is strictly better than showing nothing.
    // NOTE (2026-08-19): actions link to an axis via `primary_axis`, NOT `axis` — an earlier version of
    // this filter tested `!x?.axis || x.axis === focus`, which matched EVERY action (none carry `axis`)
    // and so borrowed the first action's problem regardless of which axis was focused.
    const axisActions = asList(card.actions || prose?.actions).filter(
      (x) => (x?.primary_axis || x?.axis) === focus
    );
    const mainText = card.explanation
      || prose?.per_axis?.[focus]?.explanation
      || actionDiagnosis(axisActions[0])
      || '';
    const ev = card.evidence || null;
    const questions = asList(card.questions || prose?.per_axis?.[focus]?.questions).filter(q => asText(q).trim() !== '');
    const wwmm = card.what_would_make_it_matter;
    const st = STATUS_PILL[card.status];
    const actions = actionsByAxis[focus] || [];
    const p = pct(sc, peerScores[focus] || []);
    const notOwed = a.status === 'na' || ['light', 'not_owed', 'optional'].includes(a.obligation);
    const grainWord = { movie: 'script', script: 'script', act: 'act', sequence: 'sequence', scene: 'scene' }[node.type] || 'unit';
    return (
      <div ref={spotRef} className="mt-1 mb-2 ms-3 p-3" style={{ border: `1px solid ${NAVY}33`, borderLeft: `4px solid ${notOwed ? '#cbd5e1' : (BAND_COLOR[band] || NAVY)}`, borderRadius: 12, background: '#f8fbfd', scrollMarginTop: 64 }}>
        {/* Axis header — the axis NAME is the hover/click target (summary + wiki drawer). */}
        <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #e6eef3' }}>
          <InfoDot article={wikiArticle('axis', focus)} onOpen={setWiki} size={15}>
            <span style={{ fontWeight: 800, color: notOwed ? '#64748b' : NAVY, fontSize: '1.02rem', borderBottom: `1px dotted ${NAVY}66` }}>{AXIS_LABEL[focus] || focus}</span>
          </InfoDot>
          {sc != null && <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '.95rem' }}>{fmtScore(sc)}</span>}
          <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>{AXIS_INFO[focus]?.lens} axis</span>
        </div>
        {/* obligation / status context row */}
        {(a.obligation || st) && (
          <div className="d-flex align-items-baseline flex-wrap" style={{ gap: 8 }}>
            {a.obligation && <span style={{ color: '#64748b', fontSize: '.8rem' }}>{a.obligation}{p != null ? ` · ${ordinal(p)} pct vs ${node.type || 'unit'}s` : ''}</span>}
            {st && !notOwed && <span style={{ marginLeft: 'auto', fontWeight: 600, fontSize: '.7rem', color: st.fg, background: st.bg, border: `1px solid ${st.bd}`, borderRadius: 999, padding: '.08rem .5rem' }}>{st.label}</span>}
          </div>
        )}
        {notOwed && (
          <div className="mt-2 p-2" style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, color: '#475569', fontSize: '.85rem', lineHeight: 1.45 }}>
            <span style={{ fontWeight: 700, color: '#334155' }}>Low is fine here.</span> This {grainWord} doesn’t owe {AXIS_LABEL[focus] || focus} — read the score for completeness, not concern.
          </div>
        )}
        {!notOwed && Array.isArray(a.rc) && a.rc.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {a.rc.map((code, i) => {
              const r = reasonCode(code);
              return (
                <div key={code} style={{ marginTop: i ? 6 : 0, paddingLeft: 10, borderLeft: `3px solid ${ROSE}55` }}>
                  <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '.9rem' }}>{r.label}</div>
                  {r.test && <div style={{ color: '#475569', fontSize: '.82rem', lineHeight: 1.4 }}>{r.test}</div>}
                </div>
              );
            })}
            {a.capped_by && a.s_rubric != null && (
              <div style={{ marginTop: 6, fontSize: '.72rem', color: '#94a3b8' }}>
                Gated to {sc != null ? fmtScore(sc) : ''} by the above — the rubric read {fmtScore(a.s_rubric)}.
              </div>
            )}
          </div>
        )}
        {mainText && <div style={{ marginTop: 6, color: '#0f172a', lineHeight: 1.5 }}>{mainText}</div>}
        {ev && (ev.quote || ev.beat_reference) && (
          <div style={{ marginTop: 8, paddingLeft: 10, borderLeft: '3px solid #d8e3ea', color: '#475569', fontSize: '.86rem', lineHeight: 1.45 }}>
            {ev.kind === 'verbatim_quote' && ev.quote ? <span style={{ fontStyle: 'italic' }}>“{ev.quote}”</span> : ev.beat_reference}
            {ev.receipt_status === 'unverified' && <span style={{ color: '#94a3b8', fontSize: '.72rem' }}> · unverified</span>}
          </div>
        )}
        {wwmm && <div style={{ marginTop: 8, color: '#475569', fontSize: '.86rem', lineHeight: 1.45 }}><span style={{ fontWeight: 600 }}>What would make it matter: </span>{wwmm}</div>}
        {/* CONVERGENCE — after this axis's own explanation and evidence, never before:
            ordered this way it reads as "another reading independently noticed this too"
            (corroboration); ordered first it reads as a feature badge. Human-approved
            pairings only, straight from the gate. */}
        {(connectionsByAxis[focus] || []).map((g, gi) => (
          <ConvergenceModule key={`${g.pairing.approval.approved_key}:${gi}`} group={g}
            onEvent={sendConnectionsEvent} onShowOnPage={showOnPage}
            hidden={hiddenConn.has(g.pairing.approval.approved_key)} onHide={hideConn}
            onOpenOther={craftCriteria ? openCriterion : null} linkable={craftKeys} />
        ))}
        {/* ⛔ THE "Ask yourself:" BLOCK THAT USED TO BE HERE IS GONE, and `questions[]` is not a
            reflection field. It rendered these as inert prose, "deliberately unboxed so they never
            read as chat seeds". That framing is not supported by anything in the generator
            (owner, 2026-08-25 — checked against the prompt, not against this file):

              · schema: "5-15 word scene-grounded question. Ends with '?'. Open questions only."
                (commands/scene_design_execution_prose_v2.php:2631)
              · the angles it must mix are DIAGNOSTIC, THEORY ("why does X usually produce Y but
                here doesn't?"), APPLICATION ("how do I X without breaking Y?") and EXAMPLES/CRAFT
                ("what do similar beats in this genre do?") — three of the four are questions you
                put to something that has read a lot of screenplays. (:1883-1912)
              · at Functional (5–6) the prompt says outright "the questions ARE the writer's path
                forward", because that band often has no craft_choice beside them.
              · rule R8.1.a (:2344) strips the one self-contained angle — a question naming a
                specific move must be promoted to craft_choices or dropped — so what survives
                skews further toward "ask someone".

            They are now the AI chips below. The templated pair is the FALLBACK for axes with none,
            which is the 7–10 band where the prompt emits zero by design. */}
        {!notOwed && actions.length > 0 && (
          <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', alignItems: 'flex-start' }}>
            {/* Repair / strengthen / protect all open the same option modal; the button language
                carries urgency so optional lifts do not read as broken. */}
            {actions.map((action, ai) => {
              const intent = actionIntent(action);
              const problemType = intent === 'repair'
                ? ((Array.isArray(a.rc) && a.rc[ai]) ? reasonCode(a.rc[ai]).label
                  : (Array.isArray(a.rc) && a.rc.length ? reasonCode(a.rc[0]).label : null))
                : null;
              const multi = actions.length > 1;
              return (
                <div key={action.slug || `${intent}-${ai}`} style={{ width: '100%' }}>
                  {multi && (
                    <div style={{ color: '#0f172a', fontSize: '.86rem', lineHeight: 1.4, marginBottom: '.25rem' }}>
                      {action.problem || action.label}
                    </div>
                  )}
                  <button type="button"
                    onClick={() => setModal({ entry: action, problem: actionDiagnosis(action, mainText), problemType })}
                    style={actionButtonStyle(action)}>
                    {actionButtonText(action)} →
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {/* NO DEAD ENDS: two starting questions the writer can hand to the AI + an open box. The first
            teaches-by-diagnosing THIS unit (works at any score); the second flexes by band — lift when
            there's room, protect when it's strong, optional push when it's only lightly owed. */}
        {/* ⚠️ ALSO RENDERS WITH CHAT OFF, DISABLED, AS A TEASER (owner, 2026-09-02). The landing-page
            snapshot passes onDiscussAxis={null}, so this whole block used to vanish — and it is one
            of the more persuasive things in the panel: per-scene questions the product has already
            written for you. Showing them inert, with one line saying what they normally do, sells
            better than hiding them. Same pattern as `readOnly` on the generate affordance above.
            Gated on readOnly rather than rendering always, so a genuinely broken chat wiring on a
            paid analysis still fails loudly instead of quietly looking like a demo. */}
        {(onDiscussAxis || readOnly) && (() => {
          const askDisabled = !onDiscussAxis;
          const label = AXIS_LABEL[focus] || focus;
          const unit = node.title || node.label || `this ${grainWord}`;
          const q1 = `Walk me through ${label} in ${unit}: where it's working, where it falls short, and what makes the difference.`;
          const q2 = notOwed
            ? `${label} is only lightly owed here, so a low score is fine — but if I wanted to lean into it, how would I?`
            : (sc != null && sc >= 7)
              ? `Why does ${label} land in ${unit}, and how do I keep it while I revise other things?`
              : `How do I lift ${label} in ${unit}? Give me a few concrete, page-level options.`;
          const chipStyle = { textAlign: 'left', background: '#f8fbfd', color: NAVY, border: `1px solid ${NAVY}2e`, borderRadius: 8, padding: '.4rem .75rem', fontSize: '.84rem', lineHeight: 1.4, cursor: 'pointer' };
          // ⚠️ BOTH SETS, IN TWO REGISTERS — see the note where "Ask yourself:" used to be.
          // `questions[]` is per-axis, per-unit and grounded in this scene's specifics, so those
          // stay full sentences: they have to be read. q1/q2 are templated and identical in shape
          // on every axis of every unit, and that sameness is their value — they become muscle
          // memory (owner: "people are used to them and easy to reach for"). So they collapse to
          // SHORT STANDING LABELS on their own row. The full question is still what gets sent; the
          // label is only the handle, and `title` exposes the sentence on hover.
          // Making the specifics REPLACE them (the first cut of this) lost the reach-without-
          // reading property; stacking all five as identical chips loses it too, because the
          // familiar two stop being findable at a glance.
          const asked = questions.map(q => asText(q)).filter(Boolean).slice(0, 3);
          // ⚠️ THE LABEL NAMES THE AXIS. "Walk me through it" / "How do I lift it?" left "it" with
          // no referent in a row of chips sitting under two scene-specific questions (owner,
          // 2026-08-25). A standing action has to be readable on its own, out of the corner of the
          // eye — that is the whole reason for making it standing.
          const standing = [
            [`Walk me through ${label}`, q1],
            [notOwed
              ? `How would I lean into ${label}?`
              : ((sc != null && sc >= 7) ? `How do I keep ${label}?` : `How do I lift ${label}?`), q2],
          ];
          // ⚠️ BORDERLESS, AND THAT IS THE WHOLE POINT (owner, 2026-08-25: "they don't look that
          // different"). The first attempt differentiated the two registers by WIDTH and FONT SIZE
          // inside the same bordered chip — far too subtle; at a glance it read as four questions
          // in one list, which is the thing this was supposed to avoid.
          // The specifics are CONTENT (sentences to read); the standing pair are CONTROLS (handles
          // you reach for). So they drop the box entirely and join `Ask your own →` on one row as
          // plain text actions — same family, same weight, same colour. The dotted underline says
          // "clickable" without drawing a second card. Also no 💬: `Ask your own →` never had one
          // and it posts to the same chat, so the glyph was never the thing marking an action.
          // ⚠️ MUTED AND LIGHT, NOT NAVY AND BOLD. Two earlier attempts changed only the CONTAINER
          // (narrower chip, then borderless) and left the type identical to the question chips —
          // same navy, same semibold, same size — so they still read as more questions (owner,
          // twice: "they don't look that different"). The register has to change in the TYPE.
          const stdStyle = {
            background: 'transparent', color: '#5b7481', border: 'none', padding: '.05rem 0',
            fontSize: '.78rem', fontWeight: 400, cursor: 'pointer',
            textDecoration: 'underline', textDecorationColor: '#c2d0d7', textUnderlineOffset: 3,
          };
          return (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ fontWeight: 600, color: NAVY, fontSize: '.86rem' }}>Ask the AI</div>
                {/* The chips are inert with chat off. Badge them BEFORE the click (owner,
                    2026-09-23) — the flash-on-click line below only reached people who tried. */}
                {askDisabled && (
                  <span style={{ fontWeight: 700, fontSize: '.66rem', color: NAVY, background: '#dbe9f2', borderRadius: 999, padding: '.1rem .45rem' }}>preview</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                {asked.map((q, i) => {
                  // ⚠️ Sent-this-session chips go inert and dim, but KEEP THE QUESTION TEXT — the
                  // chip's whole content is the question, so replacing it with "Asked" would throw
                  // away the thing worth reading. ✓ replaces 💬; see ASKED_QUESTIONS above.
                  const sent = ASKED_QUESTIONS.has(askedKey(node, focus, q));
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={sent}
                      aria-disabled={askDisabled || undefined}
                      title={sent ? 'Already asked — see the chat' : undefined}
                      onClick={() => {
                        if (askDisabled) { flashAskNote(); return; }
                        if (sent) return;
                        ASKED_QUESTIONS.add(askedKey(node, focus, q));
                        bumpAsked(n => n + 1);
                        onDiscussAxis(focus, q);
                      }}
                      style={sent
                        ? { ...chipStyle, background: '#f4f7f9', borderColor: '#e0e8ec', color: '#7b8f9a', cursor: 'default' }
                        : (askDisabled ? { ...chipStyle, cursor: 'default', opacity: .72 } : chipStyle)}
                    >
                      {sent ? '✓' : '💬'} {q}
                    </button>
                  );
                })}
                {/* One row of standing text actions — the two defaults and "Ask your own", which
                    are the same KIND of thing and now finally look it. Middots separate them. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 7, marginTop: 7 }}>
                  <span style={{ fontSize: '.78rem', color: '#8fa3ad' }}>or&nbsp;ask:</span>
                  {standing.map(([label, q], i) => {
                    const sent = ASKED_QUESTIONS.has(askedKey(node, focus, q));
                    return (
                      <React.Fragment key={`std${i}`}>
                        {i > 0 && <span style={{ color: '#c2d0d7', fontSize: '.72rem' }}>·</span>}
                        <button
                          type="button"
                          disabled={sent}
                          aria-disabled={askDisabled || undefined}
                          // ⚠️ This bare `title` is PRE-EXISTING and deliberate in live mode: the
                          // label is a short handle and the full question is exposed on hover (see
                          // the note above). It is still a rule-7 violation and wants a styled
                          // tooltip; left alone because it predates this change and the component
                          // is shared with the analysis page. Dropped in DEMO mode, where the
                          // question is never sent, so the hover buys nothing and would only be a
                          // slower second explanation competing with the always-visible line below.
                          title={askDisabled ? undefined : (sent ? 'Already asked — see the chat' : q)}
                          onClick={() => {
                            if (askDisabled) { flashAskNote(); return; }
                            if (sent) return;
                            ASKED_QUESTIONS.add(askedKey(node, focus, q));
                            bumpAsked(n => n + 1);
                            onDiscussAxis(focus, q);
                          }}
                          style={sent
                            ? { ...stdStyle, color: '#94a3b8', cursor: 'default', textDecoration: 'none' }
                            : stdStyle}
                        >
                          {sent ? `✓ ${label}` : label}
                        </button>
                      </React.Fragment>
                    );
                  })}
                  <span style={{ color: '#c2d0d7', fontSize: '.72rem' }}>·</span>
                  <button type="button" aria-disabled={askDisabled || undefined}
                    onClick={() => { if (askDisabled) { flashAskNote(); return; } onDiscussAxis(focus, false); }}
                    style={{ ...stdStyle, textDecoration: 'none' }}>Ask your own →</button>
                </div>
                {/* ⚠️ THE EXPLANATION IS ALWAYS VISIBLE, NEVER A TOOLTIP. First cut hung it off
                    `title` on each control, which broke two rules at once: bare `title` is banned
                    outright (CLAUDE.md UI rule 7 — the native tip waits ~1s, renders tiny and
                    unstyled, and reads as a bug on a designed page), and the controls were
                    `disabled`, which suppresses click events entirely — so a tap produced nothing
                    at all and on touch there is no hover to fall back on.
                    Now: the controls stay focusable with aria-disabled, a click is CAUGHT and
                    flashes this line, and the line is on screen from the start so nobody has to
                    discover it. No tooltip machinery needed, and nothing to initialise. */}
                {askDisabled && (
                  <p
                    ref={askNoteRef}
                    style={{
                      margin: '10px 0 0', fontSize: '.82rem', lineHeight: 1.5,
                      color: askNoteLit ? NAVY : '#7b8f9a',
                      background: askNoteLit ? '#eef6fa' : 'transparent',
                      borderRadius: 6, padding: askNoteLit ? '.4rem .55rem' : 0,
                      transition: 'background .18s, color .18s, padding .18s',
                    }}
                  >
                    Your own analysis writes questions like these for every scene, and each one
                    opens a chat about that scene. Here they are only a preview.
                  </p>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };
  const identityLabel = unitDisplayLabel(node, 'list');
  const titleLine = unitTitle(node);
  // "Read the text" surface. Scenes carry their own text; a SEQUENCE doesn't, but it carries its child
  // scenes (each WITH full text), so we join them into one readable block — the whole stretch, in order,
  // scene by scene. Short enough to read inline (owner). Acts are long, so they stay summary-only for now.
  const readText = useMemo(() => {
    if (node.text) return node.text;
    if (node.type === 'sequence' && Array.isArray(node.sceneTexts)) {
      return node.sceneTexts
        .map(s => (s.label ? `${s.label}\n` : '') + s.text)
        .filter(Boolean)
        .join('\n\n');
    }
    return '';
  }, [node]);
  // A stage play's `sequence` IS the playwright's scene and its `scene` rows are our units, so
  // "Read the scenes" on a printed scene, and "Read the scene" on a unit, are both the wrong noun.
  // grainNouns falls back to the feature words, so a screenplay keeps the exact strings it had.
  const readLabel = node.type === 'sequence'
    ? `Read the ${grainPlural(script, 'scene', 'scenes')}`
    : (node.type === 'scene' ? `Read the ${grainSingular(script, 'scene', 'scene')}` : 'Read the text');
  // Strip vocabulary matches the instruments (owner, 2026-08-12): the first tab
  // IS the mechanics reading, so it says so — 'diagnosis' stays as the internal
  // tab key to keep the diff surgical. MUST be defined AFTER readLabel:
  // babel compiles const to var, so referencing it earlier silently yields an
  // undefined tab label (an invisible "Read the scene" button) instead of a crash.
  //
  // "Mechanics", not "Scene Mechanics" (owner, 2026-08-21): this panel opens on scripts, acts and
  // sequences too, so the scene-scoped name was wrong on most units. NOTE the product name is still
  // "Scene Mechanics" in `ConnectionsTab.js:122` ("Also identified in …") and `pairingGate.js` —
  // those were left alone deliberately; renaming the instrument everywhere is a separate call.
  //
  // The Craft Read tab is offered when the unit HAS craft criteria, and also whenever it is the
  // currently selected tab — otherwise switching between scenes makes the selected tab vanish from
  // the strip for the length of the fetch and the whole control jumps.
  // ── INSTRUMENT SWITCHER ──────────────────────────────────────────────────────────────────────
  // Lives ON the unit's title line, right-aligned (owner, 2026-08-21). It used to be its own row
  // directly beneath the page-level tabs (Findings | Axes | Patterns | Shape), and two horizontal
  // strips stacked at the same left margin read as ONE eight-item menu — the two nearest targets
  // being the ones most easily confused. An earlier pass tried to separate them by IDIOM (link tabs
  // above, segmented control below); idiom is too weak a cue at that proximity.
  //
  // Sitting on the title line fixes both halves of the complaint at once:
  //   SCOPE   — the control is physically attached to "Scene 7 · A Job and a Lonely Call", so it
  //             obviously reads THIS unit, while the strip above reads the whole script.
  //   SPACE   — it costs no row of its own, which matters because the embedded pane is short.
  // It also no longer shares an x-range with the page tabs, which is what made misclicks easy.
  //
  // ⚠️ ALL THREE ARE ONE CONTROL. A first pass split the text out as its own toggle, on the argument
  // that it is not a "reading" of the unit but the unit itself. The owner rejected that, correctly:
  // whatever the conceptual difference, all three swap the SAME region in the SAME way, so they are
  // three tabs — and giving one of them a different affordance while it behaves identically breaks
  // the promise that same behaviour looks the same. It also read as "three tab items, one pretending
  // not to be". Conceptual taxonomy does not outrank observed behaviour. Do not re-split it.
  const tabList = [['diagnosis', 'Mechanics'],
    ...((craftCriteria || tab === 'craftread') ? [['craftread', 'Craft Read']] : []),
    ...(readText ? [['text', 'Text', readLabel]] : [])];
  // ⚠️ ONE TAB IS NOT A CONTROL — RENDER NOTHING (owner, 2026-09-01: "don't show it on script or
  // act when there's nothing else"). Craft Read is offered only when the unit HAS craft criteria and
  // Text only when it has reading text, so at script and act grain the strip frequently collapses to
  // "Mechanics" alone. A lone tab is a chooser with nothing to choose: it invites a click that
  // changes nothing, and it costs a corner of the title row on every unit that has no alternative
  // view. It was tolerable while the selected tab was a near-invisible white thumb; once selection
  // became a filled navy pill (see below) the single item read as a primary action BUTTON, which is
  // worse than useless — it is a false affordance.
  const instrumentSwitch = tabList.length < 2 ? null : (
    <div role="tablist" aria-label="Instrument view"
      style={{ display: 'inline-flex', gap: 3, background: '#e8edf2', borderRadius: 9, padding: 3 }}>
      {tabList.map(([t, lbl, hint]) => (
        <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} {...tip(hint || null)}
          style={{ border: 'none', borderRadius: 7, padding: '.35rem .85rem', fontWeight: 700, fontSize: '.88rem',
            // #5b6b7a on the #e8edf2 track was borderline at this size; the unselected label still
            // has to be comfortably readable, since it is the thing you are being invited to click.
            //
            // ⚠️ SELECTED = FILLED NAVY, NOT WHITE (owner, 2026-09-01: "it's light now"). The old
            // active state was #fff on the #e8edf2 track — roughly a 7% lightness step, carried by
            // a 0.18-alpha shadow — so SELECTION WAS SIGNALLED BY REMOVING COLOUR and the active
            // tab was the lightest thing in its own control. At a glance the whole strip read as
            // one grey blob and the two UNSELECTED labels had as much presence as the selected
            // one. Filling it inverts that: the current view is now the heaviest element.
            // It is also the same treatment as the landing page's walk chips
            // (.lr-snap__chip.is-active, same #14536e), so the one pattern means one thing across
            // the product. Do not go back to a white thumb here — the iOS segmented idiom needs a
            // much stronger shadow than this surface can carry.
            background: tab === t ? NAVY : 'transparent', color: tab === t ? '#fff' : '#47586a',
            boxShadow: tab === t ? '0 1px 3px rgba(15,23,42,.25)' : 'none', cursor: 'pointer',
            whiteSpace: 'nowrap', transition: 'background .12s, color .12s' }}>
          {lbl}
        </button>
      ))}
    </div>
  );
  // One header for EVERY mode. Before this the identity was rendered three different ways
  // (headerBlock / craftHeader / readingIdentity) and only the diagnosis one carried the switcher,
  // so switching instruments moved the furniture around. `compact` is the reading-text variant,
  // which keeps the identity to a single line.
  const unitHeader = (compact = false) => (
    <div className={compact ? 'mt-2' : 'mt-3'}
      style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', marginBottom: '.2rem' }}>
      <div style={{ minWidth: 0 }}>
        {identityLabel && <div style={{ color: NAVY, fontWeight: 600, fontSize: compact ? '.9rem' : '.92rem' }}>{identityLabel}</div>}
        {compact
          ? (titleLine ? <span style={{ color: '#475569', fontSize: '.9rem' }}>{titleLine}</span> : null)
          : ((displayTitle || !identityLabel) && <div style={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', lineHeight: 1.2, marginTop: 2 }}>{displayTitle}</div>)}
      </div>
      {instrumentSwitch && (
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {instrumentSwitch}
        </div>
      )}
    </div>
  );
  const readingText = tab === 'text' && !!readText;   // collapse the diagnostic chrome while reading
  // Gated on the NODE TYPE, not on `craftCriteria` — the criteria arrive from a fetch, so gating on
  // them would drop the reader back to the Mechanics view for the length of every scene-to-scene
  // fetch and then snap back. Scene-only because Craft Read is a per-scene instrument.
  const showingCraft = tab === 'craftread' && node?.type === 'scene';
  const craftLoading = showingCraft && !craftCriteria;   // fetch in flight, or this scene has none
  // Generated orientation TITLE (der_unit_summaries) takes over the header/hover when present; otherwise
  // we fall back to the existing title (a dramatic question on sequences). Concise, orients at a glance.
  const displayTitle = node.unit_title || titleLine || node.title || 'Unit';
  // Scene role (the mechanism enum) → its teaching article. ONLY for scenes — role/mechanism
  // is a scene-level concept, so the script/act/sequence must not show a "Type:" line (the
  // guide copy even says "the scene", which is wrong at those grains).
  const roleArticle = (node.type === 'scene' && node.mechanism && ROLES[node.mechanism]) ? ROLES[node.mechanism] : null;
  // Map the internal band to the spectrum's key set (heavy → cut_or_combine; the key is a wiki slug
  // and stays, but its COPY no longer makes a cut/merge claim — see wikiContent.js).
  const SPECTRUM_BAND = { keep: 'keep', polish: 'polish', rework: 'rework', heavy: 'cut_or_combine' };
  const spectrumBand = SPECTRUM_BAND[gv.band] || gv.band;
  // A scene/sequence is graded IN ISOLATION (`run_stage0.php:810-816` hands the grader one scene row
  // and nothing else), so at the worst band we must not frame the result as a rework prescription —
  // there is no boundary evidence behind it. Say what was actually read, and say what wasn't.
  const isolatedGrain = grain === 'scene' || grain === 'sequence';
  const standaloneOnly = isolatedGrain && gv.band === 'heavy';
  const verdictLabel = standaloneOnly ? 'Standalone read:' : 'Rework needed:';
  const structuralFitNote = standaloneOnly ? (
    <div style={{ marginTop: 3, fontSize: '.92rem', color: '#475569' }}>
      <span style={{ fontWeight: 600, color: '#334155' }}>Structural fit:</span> not assessed —
      this read didn’t see the {grain === 'scene' ? 'scenes' : 'units'} around it.
    </div>
  ) : null;

  // Shared header (identity + title + type + verdict) and exec summary — used by both layouts.
  const headerBlock = (
    <div>
      {/* Identity + instrument switcher on one line — same component all three modes use, so
          nothing shifts position when you switch instrument. */}
      {unitHeader(false)}
      {roleArticle && (
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 6, marginTop: 7, fontSize: '.92rem', color: '#475569' }}>
          <span><span style={{ fontWeight: 600, color: '#334155' }}>Type:</span> {roleArticle.label}</span>
          <InfoDot article={{ ...roleArticle, kicker: 'Scene role' }} onOpen={setWiki} />
        </div>
      )}
      {gv.band && <div className="mt-1"><VerdictSpectrum band={spectrumBand} label={verdictLabel} onOpenWiki={setWiki} />{structuralFitNote}</div>}
      {/* ◇ shared findings — discovery entry point for convergence; picking one focuses its axis */}
      {connectionsPairings && (
        <div style={{ marginTop: 8 }}>
          <SharedFindingsChip pairings={connectionsPairings} hiddenKeys={hiddenConn} onPick={(axis) => setLocalFocus(axis)} />
        </div>
      )}
      {/* Zoom in (owner, 2026-10-14): clicking a unit OPENS it and never zooms. This is the keyboard-
          reachable twin of the map cell's "Zoom in" pill, with the same name and the same magnifier. */}
      {(node.type === 'act' || node.type === 'sequence') && onFocusAct && (
        <button type="button" onClick={() => onFocusAct(node.id)} {...tip(`Widen this ${node.type} on the map so its scenes are easier to read`)}
          style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            border: `1.5px solid ${NAVY}`, background: '#eef4f8', color: NAVY, borderRadius: 8, padding: '.35rem .8rem', fontSize: '.9rem', fontWeight: 700 }}>
          <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true"><circle cx="6.5" cy="6.5" r="5" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M10.4 10.4 L14.8 14.8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /><path d="M4 6.5h5M6.5 4v5" stroke="currentColor" strokeWidth="1.8" /></svg>
          Zoom in on this {node.type}
        </button>
      )}
    </div>
  );
  // ORIENTATION — "What happens" (der_unit_summaries / scene facts). Plain story, no scores.
  //
  // ⚠️ THIS RENDERS *BELOW* THE READ, not above it (owner, 2026-09-04). It used to lead the panel,
  // which spent the most valuable space on the page telling a writer what happens in their own
  // scene — the one thing on the panel they already know. The read is the finding; it leads. This
  // is confirmation that we read the right scene, so it sits under the finding and stays folded.
  //
  // Collapsed to two lines by default for the same reason, and now regardless of length: the
  // toggle used to appear only above 130 chars, so a short recap was permanently expanded and
  // still out-ranked the read. The clamp is cosmetic below two lines anyway.
  const csum = node.content_summary || '';
  const csumLong = csum.length > 130;   // ~2 lines at this measure → the clamp actually bites
  const contentSummaryBlock = csum ? (
    <div className="mt-4">
      {/* Thumbs sits beside the LABEL, not trailing the full paragraph below (owner, 2026-09-21:
          the old position read as "is this rating the summary or the whole panel?"). It rates
          this recap specifically — a DIFFERENT generated artefact from the read above it (unit
          summaries pass for sequences/acts, summaryprompt.facts for scenes) — so it needs to be
          visibly anchored to "What happens," not floating at the bottom of the block. */}
      <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8, marginBottom: '.35rem' }}>
        <div style={{ color: '#5b7683', fontWeight: 600, fontSize: '.82rem' }}>What happens</div>
        <Thumbs key={`sum:${node.type}:${node.number ?? ''}`}
          area={thumbKey('lr:summary', node.type === 'movie' ? 'script' : node.type, node.number ?? null)}
          movieHash={movieHash} label="Does this match your script?" compact />
      </div>
      <div style={{ color: '#475569', fontSize: '.9rem', lineHeight: 1.5,
        ...((!sumOpen && csumLong) ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}) }}>
        {csum}
      </div>
      {csumLong && (
        <button type="button" onClick={() => setSumOpen(o => !o)}
          style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, fontSize: '.82rem', marginTop: '.25rem', textDecoration: 'underline', textUnderlineOffset: 2 }}>
          {sumOpen ? 'Show less' : 'Read the full recap'}
        </button>
      )}
    </div>
  ) : null;
  // THE READ — the finding, and the reason the panel exists. It now LEADS the panel (owner,
  // 2026-09-04); see the note on contentSummaryBlock above for why the recap no longer does.
  //
  // ⚠️ The "The read" eyebrow that used to sit above the headline is GONE, deliberately. It was
  // exactly the pattern CLAUDE.md UI rule 6 forbids: a small label delaying the sentence that
  // actually says something, so the reader holds a fragment before anything is said. The headline
  // IS the heading now, at 1.15rem, and the band-coloured left edge carries the categorical
  // information the label never did.
  const readAccent = BAND_STYLE[gv.band]?.fg || NAVY;
  // `verdict.reason_one_line` replaces `node.synthesis` here: both were near-duplicate one-liners
  // about the same unit (2026-09-21 finding — node.headline, node.synthesis and reason_one_line
  // all said roughly the same thing). reason_one_line is the field der_view.php's own comments
  // call the real verdict sentence; synthesis was the redundant one.
  const summaryBlock = (node.headline || verdict.reason_one_line) ? (
    <div className="mt-4 p-4" style={{ background: '#f8fbfd', border: '1px solid #e7edf1',
      borderLeft: `4px solid ${readAccent}`, borderRadius: 12 }}>
      {node.headline && <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.35 }}>{node.headline}</div>}
      {verdict.reason_one_line && <div style={{ color: '#334155', fontSize: '.95rem', lineHeight: 1.55, marginTop: node.headline ? '.45rem' : 0 }}>{verdict.reason_one_line}</div>}
    </div>
  ) : null;
  // AXIS PILLS — a fieldset of tabs over every axis prose actually flagged (primary_problem /
  // secondary_problem), capped to PILL_CAP (see problemAxes above). Opens to the worst-percentile
  // axis by default (owner call, 2026-09-21 — see the reset effect above for why this is exempt
  // from the C8-SAFE "opens neutral" rule that still governs the 12-axis rail/spotlight). One
  // badge marks the axis behind the top-ranked repair action, which may or may not be the same
  // axis as the one open by default — severity and "what to fix first" are different signals.
  // Each axis carries its own (i): hover for the plain-language definition, click to open the
  // full article in the wiki drawer (InfoUI.js). Shared by both layout branches (2026-09-21 fix —
  // this used to live only in the single-column branch, which the Timeline's twocol panel never
  // renders, so it never appeared there).
  // Boxed again (owner, 2026-09-22 — "why don't the axes have a border as we discussed"), but
  // deliberately NOT the same recipe as summaryBlock: no tinted background, no left accent bar.
  // The earlier "blends together" problem wasn't the border itself, it was this box looking like
  // an identical twin of the verdict card right above it. A plain outlined panel next to a
  // tinted, accent-barred callout reads as two different KINDS of thing (a verdict vs. a
  // worksheet), not two copies of the same thing — border contrast (#64748b, ~4.8:1) unchanged
  // from the original fix.
  const axisPillsBlock = problemAxes.length > 0 ? (
    <div className="mt-4 p-4" style={{ border: '1px solid #64748b', borderRadius: 12 }}>
      <div style={{ color: '#5b7683', fontWeight: 600, fontSize: '.82rem', marginBottom: '.55rem' }}>
        {problemAxes.length} {problemAxes.length === 1 ? 'axis' : 'axes'} below target here — tap one for why:
      </div>
      <div className="d-flex flex-wrap" style={{ gap: 6 }}>
        {shownAxes.map(ax => {
          const isPrimary = prose.axes[ax].status === 'primary_problem';
          const isStart = !!topRepair && topRepair.primary_axis === ax;
          const isOpen = openPill === ax;
          return (
            <div key={ax} role="tab" aria-selected={isOpen} tabIndex={0}
              // A pill does exactly ONE thing: opens its card (owner, 2026-09-22 -- "clicking pills
              // SHOULD NEVER do two things"). Jumping to the axis in the rail below is the card's
              // own "Go to axes" link, which every pill reaches by being opened.
              onClick={() => setOpenPill(ax)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenPill(ax); } }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                border: `1px solid ${isOpen ? NAVY : (isPrimary ? '#f0cdc9' : '#f0d8a8')}`,
                background: isOpen ? '#eef4f7' : (isPrimary ? '#fff5f4' : '#fff7e6'),
                borderRadius: 999, padding: '.28rem .65rem' }}>
              <span aria-hidden style={{ color: isPrimary ? ROSE : AMBER, fontSize: '.7rem' }}>{isPrimary ? '●' : '○'}</span>
              <span style={{ fontWeight: 600, fontSize: '.82rem', color: '#0f172a' }}>{AXIS_LABEL[ax] || ax}</span>
              {isStart && (
                <span style={{ fontWeight: 700, fontSize: '.66rem', color: NAVY, background: '#dbe9f2', borderRadius: 999, padding: '.05rem .4rem' }}>start here</span>
              )}
              <InfoDot article={wikiArticle('axis', ax)} onOpen={setWiki} size={13} />
            </div>
          );
        })}
      </div>
      {hiddenAxes.length > 0 && (
        <div style={{ marginTop: '.35rem', color: '#64748b', fontSize: '.76rem' }}>
          +{hiddenAxes.length} more also flagged:{' '}
          {hiddenAxes.map((a, i) => (
            <React.Fragment key={a}>
              {i > 0 && ', '}
              <button type="button" onClick={() => goToAxis(a)} {...tip(`Go to ${AXIS_LABEL[a] || a} in the axes below`)}
                style={{ all: 'unset', cursor: 'pointer', color: '#64748b', fontSize: '.76rem', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                {AXIS_LABEL[a] || a}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
      {openPill && problemAxes.includes(openPill) && (() => {
        const card = prose.axes[openPill] || {};
        const axAction = (actionsByAxis[openPill] || []).find(e => actionIntent(e) === 'repair') || (actionsByAxis[openPill] || [])[0] || null;
        const liftAxes = axAction ? asList(axAction.secondary_axes).map(asText).filter(Boolean) : [];
        const problem = actionDiagnosis(axAction, card.explanation || card.cell_note || '');
        const rcCodes = axes?.[openPill]?.rc;
        const problemType = Array.isArray(rcCodes) && rcCodes.length ? reasonCode(rcCodes[0]).label : null;
        return (
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e6eef3' }}>
            {card.cell_note && <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '.92rem' }}>{card.cell_note}</div>}
            {card.explanation && <div style={{ marginTop: '.35rem', color: '#334155', fontSize: '.88rem', lineHeight: 1.5 }}>{card.explanation}</div>}
            {axAction && liftAxes.length > 0 && (
              <div style={{ marginTop: '.5rem', color: '#64748b', fontSize: '.8rem' }}>
                Fixing this also lifts: {liftAxes.map(a => AXIS_LABEL[a] || a).join(', ')}
              </div>
            )}
            {/* Both actions are LINKS here, not the filled/outlined `actionButtonStyle` pill
                (owner, 2026-10-02: "visually overwhelming"). That button is the panel's one real
                CTA and it already sits in this axis's spotlight card in the rail below; repeating
                it inside a bordered card inside a bordered box made it the loudest thing on screen,
                louder than the verdict. The fix link keeps primary weight (navy, 600); "Go to axes"
                is deliberately muted below it, since it only moves you down the same panel. */}
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 16, marginTop: '.6rem' }}>
              {axAction && (
                <button type="button" onClick={() => setModal({ entry: axAction, problem, problemType })}
                  style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600, fontSize: '.85rem', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                  {actionButtonText(axAction)} →
                </button>
              )}
              <button type="button" onClick={() => goToAxis(openPill)}
                style={{ all: 'unset', cursor: 'pointer', color: '#5b7683', fontWeight: 600, fontSize: '.82rem', textDecoration: 'underline', textUnderlineOffset: 3 }}>
                Go to axes ↓
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  ) : null;
  // WHAT'S WORKING / WHAT'S MISFIRING — prose.the_read, generated on every node by the same
  // der_prose pass as everything above it, previously rendered nowhere in this app (2026-09-21
  // finding, docs/DER_FINDINGS_AND_IDEAS.md). Free phrases, not axis-tagged. Shared by both
  // layout branches (see axisPillsBlock note above for why that matters).
  // GREEN (#2AA147, the file's shared "positive" colour — used elsewhere on light-tinted
  // backgrounds where it reads fine) is ~3.3:1 on plain white, below the 4.5:1 AA needs for text
  // this size. Not changing the shared constant (it's used a dozen places, most on tinted
  // backgrounds where it's fine) — READ_GREEN is a one-off darker shade for this label+glyph only.
  const READ_GREEN = '#15803d';
  const theReadFreeformBlock = (prose?.the_read && (asList(prose.the_read.working).length > 0 || asList(prose.the_read.misfiring).length > 0)) ? (
    <div className="mt-4 d-flex flex-wrap" style={{ gap: '1.75rem' }}>
      {asList(prose.the_read.working).length > 0 && (
        <div style={{ flex: '1 1 240px', minWidth: 220 }}>
          <div style={{ color: READ_GREEN, fontWeight: 700, fontSize: '.76rem', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '.4rem' }}>What's working</div>
          {asList(prose.the_read.working).map((t, i) => (
            <div key={i} className="d-flex" style={{ gap: 7, marginBottom: '.35rem', fontSize: '.88rem', color: '#334155', lineHeight: 1.4 }}>
              <span aria-hidden style={{ color: READ_GREEN, fontWeight: 700, flexShrink: 0 }}>✓</span><span>{asText(t)}</span>
            </div>
          ))}
        </div>
      )}
      {asList(prose.the_read.misfiring).length > 0 && (
        <div style={{ flex: '1 1 240px', minWidth: 220 }}>
          <div style={{ color: ROSE, fontWeight: 700, fontSize: '.76rem', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '.4rem' }}>What's misfiring</div>
          {asList(prose.the_read.misfiring).map((t, i) => (
            <div key={i} className="d-flex" style={{ gap: 7, marginBottom: '.35rem', fontSize: '.88rem', color: '#334155', lineHeight: 1.4 }}>
              <span aria-hidden style={{ color: ROSE, fontWeight: 700, flexShrink: 0 }}>✗</span><span>{asText(t)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;
  // All three modes now share one header, so the identity and the switcher never move when you
  // change instrument. (Was: tabsBlock + craftHeader + readingIdentity, three different treatments.)
  const tabsBlock = null;
  const craftHeader = unitHeader(false);
  const readingIdentity = unitHeader(true);

  // TWO-COLUMN layout (timeline): the whole read as a clickable axis rail on the left, the selected
  // axis's explanation + fixes on the right — like the legacy D&E scene modal, using the extra width.
  if (twocol) {
    // ── LENS AS A FIRST-CLASS SUBJECT ────────────────────────────────────────────────────────
    // `localFocus` holds an axis key OR a lens key; they share one slot so the rail has exactly
    // one selection and the spotlight has exactly one subject. Lens keys are 'design' /
    // 'execution' / 'read', which cannot collide with the twelve axis keys.
    const isLens = (k) => !!k && LENSES.some(L => L.key === k);
    // The lens the unit's verdict actually turns on: a broken one if there is one, else a soft
    // one, else the lowest-scoring. Same rule as LRSnapshot.jsx:113-119, deliberately.
    // ⚠️ This is a comparison ACROSS THE THREE LENS ROLLUPS, which is sanctioned — unlike ranking
    // the twelve axes against each other on raw scores, which landmine 58 forbids because one
    // cell's CI exceeds the whole map's range. Do not "improve" this into a 12-way argmin.
    const pickLens = (want) => LENSES.find(L => gv.statuses?.[L.key] === want) || null;
    const scoredLenses = LENSES.filter(L => lensScore[L.key] != null);
    const defaultLens = (pickLens('broken') || pickLens('soft')
      || scoredLenses.slice().sort((a, b) => lensScore[a.key] - lensScore[b.key])[0]
      || LENSES[0]).key;

    // The lens reading. Everything here already existed and none of it was rendered in twocol:
    // the LLM's per-unit lens summary (prose.verdict.<lens>_summary, present on every node at
    // every grain), the static LENS_MEANING backstop for unprosed scripts, the LENS_HEADLINE
    // verdict, the Solid/Soft/Broken chip, and the lens wiki article.
    const lensSpotlight = (key) => {
      const L = LENSES.find(x => x.key === key);
      if (!L) return null;
      const sc = lensScore[key];
      const st = gv.statuses?.[key] || null;
      const accent = LENS_ACCENT[key] || NAVY;
      const sm = st ? STATUS_META[st] : null;
      // LLM lens prose first, the deterministic sentence second. Never empty.
      const summary = verdict[`${key}_summary`] || (st ? LENS_MEANING[L.label]?.[st] : null);
      // Weakest and strongest OWED axis WITHIN this lens — a within-lens comparison, which is the
      // other sanctioned one. Light/not-owed axes are excluded: a low score on an axis this unit
      // does not owe is not a weakness, and saying so would be the F47 mistake.
      const owedRows = L.axes.map(ax => {
        const a = axes?.[ax];
        const s = (a && a.status !== 'na' && a.score != null) ? parseFloat(a.score) : null;
        const owed = a?.obligation === 'core' || a?.obligation === 'expected';
        return (s != null && owed) ? { ax, s } : null;
      }).filter(Boolean).sort((a, b) => a.s - b.s);
      const weakest = owedRows[0] || null;
      const strongest = owedRows.length > 1 ? owedRows[owedRows.length - 1] : null;
      const jump = (ax, label, tone) => (
        <button key={ax} type="button" onClick={() => setLocalFocus(ax)}
          {...tip(AXIS_INFO[ax]?.measures || '')}
          style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            border: `1px solid ${tone}`, borderRadius: 999, padding: '.16rem .6rem', marginRight: '.4rem',
            color: tone, fontSize: '.82rem', fontWeight: 600 }}>
          {label} {AXIS_LABEL[ax] || ax} <span style={{ fontWeight: 800 }}>{fmtScore(owedRows.find(r => r.ax === ax).s)}</span>
        </button>
      );
      return (
        <div ref={spotRef} style={{ border: '1px solid #e2e8f0', borderLeft: `4px solid ${accent}`, borderRadius: 10, padding: '.85rem 1rem' }}>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, marginBottom: '.5rem' }}>
            {/* InfoDot takes its accessible name from article.label, so no label prop — wrapping
                the heading makes "Design (i)" one target that opens the lens guide. */}
            <InfoDot article={wikiArticle('lens', key)} onOpen={setWiki}>
              <span style={{ color: accent, fontWeight: 700, fontSize: '1.15rem' }}>{L.label}</span>
            </InfoDot>
            {sc != null && (
              <span style={{ fontWeight: 800, fontSize: '1rem', color: '#fff',
                background: BAND_COLOR[BAND(sc)] || '#94a3b8', borderRadius: 999, padding: '.1rem .6rem' }}>{fmtScore(sc)}</span>
            )}
            {sm && (
              <span style={{ fontWeight: 700, fontSize: '.78rem', color: sm.fg, background: sm.bg,
                border: `1px solid ${sm.bd}`, borderRadius: 999, padding: '.08rem .5rem' }}>{sm.label}</span>
            )}
          </div>
          {st && LENS_HEADLINE[key]?.[st] && (
            <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '1.02rem', lineHeight: 1.35 }}>{LENS_HEADLINE[key][st]}</div>
          )}
          {summary && <div style={{ color: '#334155', fontSize: '.95rem', lineHeight: 1.55, marginTop: '.4rem' }}>{summary}</div>}
          {(weakest || strongest) && (
            <div style={{ marginTop: '.75rem' }}>
              {/* Label at .8rem/600 — readable, and never smaller than the chips it labels. */}
              <div style={{ color: '#5b7683', fontWeight: 600, fontSize: '.8rem', marginBottom: '.35rem' }}>
                Within {L.label}, open an axis
              </div>
              {weakest && jump(weakest.ax, '↓', ROSE)}
              {strongest && jump(strongest.ax, '↑', GREEN)}
            </div>
          )}
        </div>
      );
    };

    // The lens is the PARENT and now looks like one (owner, 2026-09-04). Three changes, all about
    // hierarchy rather than decoration:
    //   1. The name is 1.02rem/700 against the axes' .82rem (`dense`), instead of .86rem against
    //      .9rem. Before this the axis label was literally bigger than the lens it belonged to.
    //   2. The score is a .86rem pill, not .68rem — it was the smallest number in the rail while
    //      being the most aggregated one.
    //   3. The header is a real <button>. It was completely inert: no onClick, no cursor, no
    //      tooltip, no InfoDot, no role, not keyboard-reachable — so the framework's headline
    //      concept was the one thing in the panel you could not open. Clicking it puts the lens
    //      in the spotlight (see lensSpotlight).
    // The status word carries what the number cannot: `LENS_HEADLINE` is nine plain-English
    // verdicts that already shipped in the `single` branch and were never rendered here.
    const railLens = (L) => {
      const lensSc = lensScore[L.key];
      const lensSt = gv.statuses?.[L.key] || null;
      // Marked as selected when it is what the spotlight is SHOWING — including the default lens an
      // unfocused open lands on (owner, 2026-10-14: "the selected by default lens isn't marked").
      const on = (localFocus || defaultLens) === L.key;
      const accent = LENS_ACCENT[L.key] || NAVY;
      const rows = L.axes.map(ax => {
        const a = axes?.[ax];
        const sc = a && a.status !== 'na' ? parseFloat(a.score) : null;
        const owed = a?.obligation === 'core' || a?.obligation === 'expected';
        const flagged = sc != null && owed && sc < 5;
        return { ax, a, flagged };
      });
      return (
        <div key={L.key} id={`np-lens-${L.key}`} className="mb-3" style={{ scrollMarginTop: 12 }}>
          <button type="button" onClick={() => setLocalFocus(L.key)} aria-pressed={on}
            {...tip(`What ${L.label} measures, and how this unit reads on it`)}
            className="w-100 text-start d-flex align-items-center px-2 py-1"
            style={{ position: 'relative', gap: 8, border: 'none',
              borderLeft: `3px solid ${on ? accent : 'transparent'}`,
              background: on ? '#eef4f8' : 'transparent',
              borderRadius: on ? '0 7px 7px 0' : 7, cursor: 'pointer', margin: '.1rem 0 .15rem' }}>
            <span style={{ color: on ? accent : '#0f172a', fontWeight: 700, fontSize: '1.02rem', letterSpacing: '-.01em' }}>{L.label}</span>
            {lensSc != null && (
              <span style={{ fontWeight: 800, fontSize: '.86rem', color: '#fff',
                background: BAND_COLOR[BAND(lensSc)] || '#94a3b8', borderRadius: 999, padding: '.06rem .5rem' }}>{fmtScore(lensSc)}</span>
            )}
            {lensSt && LENS_HEADLINE[L.key]?.[lensSt] && (
              <span style={{ color: '#5b7683', fontSize: '.78rem', lineHeight: 1.2, flex: 1, minWidth: 0 }}>
                {LENS_HEADLINE[L.key][lensSt]}
              </span>
            )}
            {on && pointerFor(accent)}
            <span style={{ width: 14, textAlign: 'center', flexShrink: 0, color: on ? accent : '#cbd5e1', fontWeight: 800 }}>{on ? '▸' : '›'}</span>
          </button>
          {rows.map(r => (
            <AxisRow key={r.ax} axis={r.ax} dense
              ax={r.a ? { s: r.a.score, o: r.a.obligation, st: r.a.status } : null}
              peerScores={peerScores[r.ax] || []}
              onPick={setLocalFocus} active={localFocus === r.ax} pointer={twocol}
              hasRepair={!!(actionsByAxis[r.ax] && actionsByAxis[r.ax].length)} flagged={r.flagged} showAll={showAllScores}
              convCount={connCount(r.ax)} />
          ))}
        </div>
      );
    };
    const tocItems = (toc && !showingCraft && !readingText) ? [
      { id: 'np-summary', label: 'Summary' },
      axisPillsBlock ? { id: 'np-fix', label: 'Where to start' } : null,
      theReadFreeformBlock ? { id: 'np-read', label: 'Working & misfiring' } : null,
      contentSummaryBlock ? { id: 'np-happens', label: 'What happens' } : null,
      axes ? { id: 'np-axes', label: 'All the axes' } : null,
      ...(axes ? LENSES.map(L => ({ id: `np-lens-${L.key}`, label: L.label, indent: true, parent: 'np-axes', lens: L.key })) : []),
    ].filter(Boolean) : [];
    const showToc = tocItems.length > 1 && wrapW >= 1000;
    const body = (
      <div ref={rootRef} className="px-3 pb-4" style={{ maxWidth: 1180 }}>
        {tabsBlock}
        {showingCraft ? (
          // CRAFT READ: the traditional reading, same rail/spotlight idiom.
          <>
            {craftHeader}
            {craftLoading
              ? <div style={{ color: '#94a3b8', fontSize: '.9rem', padding: '1rem 0' }}>Loading the Craft Read…</div>
              : <CraftReadTab criteria={craftCriteria} overview={craftData?.overview} twocol focus={craftFocus} onFocus={setCraftFocus}
                  convByCriterion={connByCriterion} onEvent={sendConnectionsEvent} onShowOnPage={showOnPage}
                  hiddenConn={hiddenConn} onHide={hideConn} onOpenAxis={openAxis} axisLabel={(k) => AXIS_LABEL[k] || criterionLabel(k)}
                  groups={craftData?.groups || null} onAsk={askCriterion} />}
          </>
        ) : readingText ? (
          // READING MODE: just the text (plus a slim unit label) — the diagnosis chrome collapses away.
          <>
            {readingIdentity}
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '.82rem', color: '#0f172a', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', marginTop: '.4rem', lineHeight: 1.55 }}>{(connHighlight && renderHighlighted(readText, connHighlight)) || readText}</pre>
          </>
        ) : (
          <>
          {/* `measure` (the desktop unit modal): running prose is capped at a readable line length
              (~70 characters; owner 2026-10-14 — the modal ran ~150). The What's working / What's
              misfiring pair stays full width because each column is already a comfortable measure. */}
          <div style={measure ? { maxWidth: '72ch' } : undefined}>
            <div id="np-summary" style={{ scrollMarginTop: 12 }}>
            {headerBlock}
            {/* THE READ FIRST, the recap second (owner, 2026-09-04). Order is the whole point of
                this pair: the finding is what the reader came for, the recap only confirms we read
                the right unit. Do not swap these back. */}
            {summaryBlock}
            </div>
            {axisPillsBlock && <div id="np-fix" style={{ scrollMarginTop: 12 }}>{axisPillsBlock}</div>}
          </div>
          {theReadFreeformBlock && <div id="np-read" style={{ scrollMarginTop: 12 }}>{theReadFreeformBlock}</div>}
          {contentSummaryBlock && <div id="np-happens" style={{ scrollMarginTop: 12, ...(measure ? { maxWidth: '72ch' } : {}) }}>{contentSummaryBlock}</div>}
          {/* obligation-dot key — spans BOTH columns so it doesn't sit lopsided over the rail */}
          <div id="np-axes" style={{ color: '#64748b', fontSize: '.74rem', lineHeight: 1.4, marginTop: '1rem', scrollMarginTop: 12 }}>
            <span style={{ color: NAVY }}>●</span> core · <span style={{ color: '#5b7683' }}>◐</span> expected · <span style={{ color: '#8595a3' }}>○</span> light <span style={{ color: '#94a3b8' }}>—</span> how much each axis is owed here
          </div>
          {showAllToggle}

          {/* LENS ORIENTATION BAND. Full width, above BOTH columns, because what it says is about
              the RAIL below it — which lens set this unit's level and therefore which axes are
              worth opening. It used to sit inside the right-hand column, where it competed with the
              axis card for the same space and was nowhere near the thing it described.
              Set by the caller (LRSnapshot.onSelect) as node.focusAxisSuggested = {lens, text} when
              the click could not name an axis itself — i.e. Triage and lens colourings.
              PERSISTS after the reader opens a different axis: "Design is the soft lens here" does
              not stop being true because they clicked Clarity. Only the word "Suggested" is
              dropped once they have made their own choice, since by then it is orientation rather
              than a recommendation. */}
          {node?.focusAxisSuggested && (() => {
            const sug = node.focusAxisSuggested;
            const lm = LENS_META[sug.lens] || null;
            // LENS_META.color is tuned for a solid badge (white text ON the colour). Used as TEXT
            // on the pale tint it only clears WCAG AA for Design (4.51) and Read (4.87);
            // Execution's green is 3.03. Darkened to 78% for that one case — measured, not
            // eyeballed. LENS_META itself left alone: it is correct for its own use.
            const CHIP_TEXT = { Execution: '#0b7c56' };
            const accent = CHIP_TEXT[sug.lens] || (lm ? lm.color : NAVY);
            const untouched = localFocus === node.focusAxis;
            return (
              <div style={{ display: 'flex', gap: '.7rem', alignItems: 'flex-start',
                margin: '.9rem 0 0', padding: '.6rem .8rem .6rem .7rem', borderRadius: 8,
                background: lm ? lm.tint : '#eef4f7', borderLeft: `4px solid ${accent}` }}>
                <p style={{ margin: 0, color: '#3f5563', fontSize: '.86rem', lineHeight: 1.5 }}>
                  {untouched && (
                    /* .72rem, not smaller — this labels the sentence it introduces, and a label
                       must not be fainter or smaller than its content (CLAUDE.md UI rule 6). */
                    <span style={{ display: 'inline-block', marginRight: '.45rem', color: accent,
                      fontSize: '.72rem', fontWeight: 800, letterSpacing: '.05em',
                      textTransform: 'uppercase' }}>Suggested</span>
                  )}
                  <strong style={{ color: accent, fontWeight: 700 }}>{sug.lens}</strong>
                  {sug.text.replace(new RegExp('^' + sug.lens), '')}
                </p>
              </div>
            );
          })()}

          {/* np-twocol: the rail + spotlight row. The class exists ONLY so a media query can stack
              it on a phone — at 375px the rail's clamp(210px…) left the spotlight ~140px wide and
              the reading rendered one word per line, with the rail's own labels cut off at the left
              edge (owner screenshot, 2026-08-30). Kept as a class rather than an inline change so
              the analysis page's desktop layout is untouched. See grades.css. */}
          <div className="d-flex np-twocol" style={{ gap: '1.5rem', marginTop: '.6rem', alignItems: 'flex-start' }}>
            <div className="np-twocol__rail" style={{ flex: '0 0 clamp(210px, 30%, 300px)', minWidth: 0 }}>
              {axes && LENSES.map(railLens)}
            </div>
            {/* no standalone divider — the spotlight card has its own band-coloured border + the pointer connector */}
            <div style={{ flex: 1, minWidth: 0, paddingLeft: '1.5rem' }}>
              {/* The right column is ALWAYS a reading — an axis, or a LENS. It is never the bare
                  "pick something" prompt any more (owner, 2026-09-04).
                  ⚠️ WHY THIS MATTERED: `openNode` only injects a focusAxis when the map's colour-by
                  is one of the twelve axes (Icicle.js:600-602) — and Triage is the DEFAULT — so the
                  Sos2 app's "scan the rail" entry was not a rare fallback at all. It was what every
                  click landed on, out of the box, forever. The panel's whole right half opened as
                  an instruction to do more work.
                  Now an unfocused open resolves to the lens that set this unit's level, which has
                  real per-unit prose behind it (prose.verdict.<lens>_summary). */}
              {isLens(localFocus) ? lensSpotlight(localFocus)
                : localFocus ? axisSpotlight(localFocus)
                  : lensSpotlight(defaultLens)}
            </div>
          </div>
          </>
        )}
        {modal && (
          <RepairModal entry={modal.entry} problem={modal.problem} problemType={modal.problemType}
            sceneNumber={node.type === 'scene' ? node.number : null}
            movieHash={movieHash} movieId={movieId} readOnly={readOnly} onClose={() => setModal(null)} />
        )}
        <WikiDrawer article={wiki} onClose={() => setWiki(null)} onNavigate={setWiki} />
      </div>
    );
    if (!toc) return body;
    return (
      <div ref={wrapRef} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {showToc && (
          <div style={{ flex: '0 0 172px', position: 'sticky', top: 12, alignSelf: 'flex-start', paddingLeft: 12, paddingTop: 8 }}>
            <PageToc items={tocItems} activeId={tocActive} title="On this reading" stickyTop={0}
              onPick={(it) => { if (it.lens) setLocalFocus(it.lens); }} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>{body}</div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="px-3 pb-4" style={{ maxWidth: 680 }}>
      {/* No standalone tab row any more — the switcher rides on the unit header below, which is
          rendered by whichever mode is active (headerBlock / craftHeader / readingIdentity). */}
      {readingText && readingIdentity}
      {showingCraft && craftHeader}
      {showingCraft && (craftLoading
        ? <div style={{ color: '#94a3b8', fontSize: '.9rem', padding: '1rem 0' }}>Loading the Craft Read…</div>
        : <CraftReadTab criteria={craftCriteria} overview={craftData?.overview} focus={craftFocus} onFocus={setCraftFocus}
            convByCriterion={connByCriterion} onEvent={sendConnectionsEvent} onShowOnPage={showOnPage}
            hiddenConn={hiddenConn} onHide={hideConn} onOpenAxis={openAxis} axisLabel={(k) => AXIS_LABEL[k] || criterionLabel(k)}
            groups={craftData?.groups || null} onAsk={askCriterion} />
      )}
      {/* Identity — canonical unit label, then the title on its own line when there is one. */}
      {!readingText && !showingCraft && (
      <div className="mt-3">
        {identityLabel && (
          <div style={{ color: NAVY, fontWeight: 600, fontSize: '.92rem' }}>
            {identityLabel}
          </div>
        )}
        {(titleLine || !identityLabel) && (
          <div style={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', lineHeight: 1.2, marginTop: 2 }}>{titleLine || node.title || 'Unit'}</div>
        )}
        {/* Orientation line(s): Type + how-much-rework. The verdict is a SUMMARY (a different
            level than the per-lens reads below), so it lives up here with the other orienting
            (i)s — effort-framed, so leading with it orients without feeling like a cocky verdict. */}
        {roleArticle && (
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 6, marginTop: 7, fontSize: '.92rem', color: '#475569' }}>
            <span><span style={{ fontWeight: 600, color: '#334155' }}>Type:</span> {roleArticle.label}</span>
            <InfoDot article={{ ...roleArticle, kicker: 'Scene role' }} onOpen={setWiki} />
          </div>
        )}
        {gv.band && (
          <div className="mt-1">
            <VerdictSpectrum band={spectrumBand} label={verdictLabel} onOpenWiki={setWiki} />
            {structuralFitNote}
          </div>
        )}
        {/* ◇ shared findings — discovery entry point for convergence; picking one focuses its axis */}
        {connectionsPairings && (
          <div style={{ marginTop: 8 }}>
            <SharedFindingsChip pairings={connectionsPairings} hiddenKeys={hiddenConn} onPick={(axis) => setLocalFocus(axis)} />
          </div>
        )}
      </div>
      )}

      {readingText ? (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
          fontSize: '.82rem', color: '#0f172a', background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: 8, padding: '12px 14px', marginTop: '.7rem', lineHeight: 1.55 }}>{(connHighlight && renderHighlighted(readText, connHighlight)) || readText}</pre>
      ) : showingCraft ? null : (
      <>

      {/* EXECUTIVE SUMMARY — the one-glance overview, at the TOP so it frames everything below.
          `verdict.reason_one_line` replaces `node.synthesis` here: both were near-duplicate
          one-liners about the same unit (2026-09-21 finding — three overlapping sentences existed:
          node.headline, node.synthesis, verdict.reason_one_line), and reason_one_line is the field
          der_view.php's own comments call the real verdict sentence, synthesis was the redundant one. */}
      {(node.headline || verdict.reason_one_line) && (
        <div className="mt-3 p-3" style={{ background: '#f8fbfd', border: '1px solid #e7edf1', borderRadius: 12 }}>
          {node.headline && <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '1rem', lineHeight: 1.4 }}>{node.headline}</div>}
          {verdict.reason_one_line && <div style={{ color: '#475569', fontSize: '.9rem', lineHeight: 1.5, marginTop: node.headline ? '.35rem' : 0 }}>{verdict.reason_one_line}</div>}
        </div>
      )}

      {/* AXIS PILLS + WHAT'S WORKING/MISFIRING — defined once above (axisPillsBlock,
          theReadFreeformBlock) and shared with the twocol branch (2026-09-21 fix), so the two
          layouts can't drift out of sync again. */}
      {axisPillsBlock}
      {theReadFreeformBlock}

      {/* COMPACT D·E·R READ — the navigational abstract, BEFORE the lever (a fair read
          earns the critique). One lens per row: a plain-English top strength + top drag,
          the axis a clickable breadcrumb that expands its full diagnosis below. Tags come
          straight from the scoring, so they teach the framework correctly by repetition.
          This is the table of contents; the detailed lens blocks below are the "why". */}
      {axes && (
        <div className="mt-3" style={{ borderLeft: '3px solid #dbe3e9', paddingLeft: '1.05rem' }}>
          {/* ONE shared accent + label binds the three lens lines into a single "read" unit
              (Gestalt common region), so the verdict up top reads as a different kind of thing. */}
          <div style={{ color: NAVY, fontWeight: 700, fontSize: '.9rem', marginBottom: '.7rem' }}>The read</div>
          <div style={{ display: 'grid', gap: '1.25rem' }}>
          {LENSES.map(L => {
            const status = gv.statuses?.[L.key];
            const rows = L.axes.map(ax => {
              const a = axes[ax] || {};
              const sc = a.status !== 'na' && a.score != null ? parseFloat(a.score) : null;
              const notOwed = a.status === 'na' || ['light', 'not_owed', 'optional'].includes(a.obligation);
              const gloss = prose?.axes?.[ax]?.cell_note || prose?.cell_notes?.[ax] || '';
              const flagged = sc != null && !notOwed && sc < 5;
              const strong = sc != null && !notOwed && (a.status === 'working_strength' || a.status === 'protected_strength' || sc >= 6);
              return { ax, sc, notOwed, gloss, flagged, strong };
            });
            const owed = rows.filter(r => !r.notOwed && r.sc != null);
            // Flat row: only the lens NAME carries the lens colour (no dot — a coloured dot
            // here + the coloured ✓/✗ on each line below was a "christmas lights" clash).
            // The verb ("what it’s built to do") moved into the lens (i) guide.
            const lsc = lensScore[L.key];
            const headLine = (txt, muted) => (
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 6, marginBottom: '.2rem' }}>
                <span style={{ color: LENS_ACCENT[L.key], fontWeight: 700, fontSize: '.98rem' }}>{L.label}</span>
                {lsc != null && (
                  <span style={{ fontWeight: 800, fontSize: '.78rem', color: '#fff', background: BAND_COLOR[BAND(lsc)] || '#94a3b8', borderRadius: 999, padding: '.03rem .5rem' }}>{lsc.toFixed(1)}</span>
                )}
                {LENS_WIKI[L.key] && <InfoDot article={{ ...LENS_WIKI[L.key], kicker: 'Lens' }} onOpen={setWiki} />}
                {txt && <span style={{ color: muted ? '#94a3b8' : '#0f172a', fontWeight: 600, fontSize: '.98rem' }}>— {txt}</span>}
              </div>
            );
            // lens not owed at all by this scene — say so, don't imply failure
            if (owed.length === 0) {
              return <div key={L.key}>{headLine('Lightly owed — not the main job here', true)}</div>;
            }
            const drags = owed.filter(r => r.flagged).sort((a, b) => a.sc - b.sc).slice(0, 2);
            const strength = owed.filter(r => r.strong).sort((a, b) => b.sc - a.sc)[0] || null;
            return (
              <div key={L.key}>
                {headLine(LENS_HEADLINE[L.key]?.[status])}
                {strength && <ReadLine plus gloss={strength.gloss} ax={strength.ax} onPick={setLocalFocus} />}
                {drags.map(d => <ReadLine key={d.ax} plus={false} gloss={d.gloss} ax={d.ax} onPick={setLocalFocus} />)}
                {drags.length === 0 && <div style={{ color: '#94a3b8', fontSize: '.86rem', marginTop: '.55rem' }}>No major {L.label.toLowerCase()} drag.</div>}
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* The three lenses as TEACHING blocks: each lens shows what it's DOING (score +
          status + a plain-language read), then groups its axes into Needs work / Working
          with a one-line gloss each — so the panel teaches the axes, not just scores them.
          Clicking any axis expands its full diagnosis inline (fixes live in the modal). */}
      <div className="mt-3">
          <SectionLabel>The three groups</SectionLabel>
          {showAllToggle}
          <div style={{ color: '#94a3b8', fontSize: '.74rem', marginTop: '-.35rem', marginBottom: '.55rem' }}>
            ● core · ◐ expected · ○ light — percentile is vs this script's {node.type === 'movie' ? 'units' : (node.type || 'unit') + 's'} for now (corpus-wide later)
          </div>
          {LENSES.map(L => {
            const lensSc = lensScore[L.key];
            const lensBand = BAND(lensSc);
            const lensStatus = gv.statuses?.[L.key];
            const sm = lensStatus ? STATUS_META[lensStatus] : null;
            const summary = verdict[`${L.key}_summary`];
            // classify each axis row in this lens
            const rows = L.axes.map(ax => {
              const a = axes?.[ax];
              const sc = a && a.status !== 'na' ? parseFloat(a.score) : null;
              const owed = a?.obligation === 'core' || a?.obligation === 'expected';
              const notOwed = !a || a.status === 'na' || ['light', 'not_owed', 'optional'].includes(a.obligation);
              const flagged = sc != null && owed && sc < 5;
              const c = prose?.axes?.[ax] || {};
              const note = c.cell_note || (c.explanation || '').split(/[.!?]\s/)[0] || '';
              return { ax, a, sc, flagged, notOwed, note };
            });
            const needsWork = rows.filter(r => r.flagged);
            const working = rows.filter(r => !r.flagged && !r.notOwed);
            const measured = rows.filter(r => !r.flagged && r.notOwed);
            const renderAxis = (r) => (
              <React.Fragment key={r.ax}>
                <AxisRow axis={r.ax}
                  ax={r.a ? { s: r.a.score, o: r.a.obligation, st: r.a.status } : null}
                  peerScores={peerScores[r.ax] || []}
                  onPick={setLocalFocus} active={localFocus === r.ax} pointer={twocol}
                  hasRepair={!!(actionsByAxis[r.ax] && actionsByAxis[r.ax].length)} flagged={r.flagged} showAll={showAllScores}
                  convCount={connCount(r.ax)} />
                {localFocus !== r.ax && r.note && (
                  <div style={{ color: '#64748b', fontSize: '.82rem', lineHeight: 1.4, margin: '-.15rem 0 .4rem 1.5rem' }}>{r.note}</div>
                )}
                {localFocus === r.ax && axisSpotlight(r.ax)}
              </React.Fragment>
            );
            const subLabel = (txt, color) => (
              <div style={{ color, fontWeight: 600, fontSize: '.8rem', margin: '.45rem 0 .25rem .15rem' }}>{txt}</div>
            );
            return (
              <div key={L.key} className="mb-3 p-3" style={{ border: '1px solid #e8eef2', borderRadius: 12, background: '#fcfdfe' }}>
                {/* lens header — name + score + what-it-is */}
                <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ color: NAVY, fontWeight: 700, fontSize: '1rem' }}>{L.label}</span>
                  {LENS_WIKI[L.key] && <InfoDot article={{ ...LENS_WIKI[L.key], kicker: 'Lens' }} onOpen={setWiki} />}
                  {lensSc != null && (
                    <span style={{ fontWeight: 800, fontSize: '.9rem', color: '#fff', background: BAND_COLOR[lensBand] || '#94a3b8', borderRadius: 999, padding: '.08rem .6rem', marginLeft: 2 }}>{fmtScore(lensSc)}</span>
                  )}
                  {sm && <span style={{ fontWeight: 700, fontSize: '.72rem', color: sm.fg, background: sm.bg, border: `1px solid ${sm.bd}`, borderRadius: 999, padding: '.08rem .5rem' }}>{sm.label}</span>}
                </div>
                {/* what this lens is DOING in this unit */}
                {(summary || (lensStatus && LENS_MEANING[L.label]?.[lensStatus])) && (
                  <div style={{ color: '#334155', fontSize: '.92rem', lineHeight: 1.5, marginTop: '.4rem' }}>
                    {summary || LENS_MEANING[L.label][lensStatus]}
                  </div>
                )}
                {/* the axes that make up this lens, grouped so the read teaches */}
                <div className="mt-2">
                  {needsWork.length > 0 && (<>{subLabel('Needs work', ROSE)}{needsWork.map(renderAxis)}</>)}
                  {working.length > 0 && (<>{subLabel('Working', '#1f7a37')}{working.map(renderAxis)}</>)}
                  {measured.length > 0 && (<>{subLabel('Not owed here', '#94a3b8')}{measured.map(renderAxis)}</>)}
                </div>
              </div>
            );
          })}

          {/* No "Other repair paths" list, no standalone Push/Protect sections — every fix,
              push, and protect lives on its OWN axis above (one home per item). The single
              biggest move is spotlighted in "What to work on" → modal. */}
        </div>

      {/* Fallback footer when detailed prose/fixes aren't generated — the overview is already shown up
          top, so here we only surface the starting move + the "not generated yet" note. */}
      {!prose && (
        <div className="mt-3 p-3" style={{ border: '1px dashed #cbd5e1', borderRadius: 10, color: '#64748b', fontSize: '.9rem', lineHeight: 1.5 }}>
          {node.repair?.family && <div>Start with: {node.repair.family}.</div>}
          <div style={{ marginTop: node.repair?.family ? '.5rem' : 0, fontStyle: 'italic' }}>Detailed fix paths haven't been generated for this script yet.</div>
        </div>
      )}
      </>
      )}
      {modal && (
        <RepairModal entry={modal.entry} problem={modal.problem} problemType={modal.problemType}
          sceneNumber={node.type === 'scene' ? node.number : null}
          movieHash={movieHash} movieId={movieId} readOnly={readOnly} onClose={() => setModal(null)} />
      )}
      <WikiDrawer article={wiki} onClose={() => setWiki(null)} onNavigate={setWiki} />
    </div>
  );
}
NodePanel.propTypes = {
  node: PropTypes.object,
  peers: PropTypes.array,
  movieHash: PropTypes.string,
  movieId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  focusBlock: PropTypes.oneOf(['start', 'center', 'nearest', 'end']),
  layout: PropTypes.oneOf(['single', 'twocol']),
  onDiscussAxis: PropTypes.func,
  measure: PropTypes.bool,
};
